import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { StatusLabel, Text } from 'components/Text';
import { LoadingScreen } from 'components/LoadingScreen';
import { Link, useLocation, useParams } from 'react-router-dom';
import { GET_EASYSHIP_CONNECTION, GET_RETURN } from 'gql/queries';
import {
  MODIFY_RETURN_STATUS,
  CREATE_LABEL_FOR_RETURN,
  SEND_RETURN_LABEL_TO_CUSTOMER,
  CREATE_RETURN_REFUND,
  RESOLVE_RETURN_WITH_DEFLECTION,
  CREATE_STORE_CREDIT,
  MARK_RETURN_AS_RECEIVED,
  RESOLVE_RETURN_FLAG,
  RETRY_SHOPIFY_RETURN_SYNC,
  GENERATE_INSTALL_LINK,
} from 'gql/mutations';
import {
  Return,
  ReturnDynamicFee,
  ReturnDashboardFlag,
  ReturnEvent,
  ReturnStatus,
} from 'types/return';
import { useStore } from 'context/store-context';
import { Back } from 'components/Back';
import { OrderRequestsPanel } from 'components/OrderRequestsPanel';
import {
  getRequestReturnTo,
  getShopifyOrderAdminUrl,
  withRequestReturnTo,
} from 'utils/requestNavigation';
import { getRequestTypeLabel } from 'utils/requestTypePresentation';
import {
  getStoreCurrencyFormatter,
  apiErrorHandler,
  NotificationTypes,
  formatStatusText,
} from 'utils';
import { withNotification } from 'components/Notification';
import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { DenyReturn } from './DenyReturn';
import { ReturnApprovalConfirmation } from './ReturnApprovalConfirmation';
import { ReturnWarehouseRoutingPanel, returnWarehouseNeedsStaffAction } from './ReturnWarehouseRoutingPanel';
import { ReturnInspectionPanel, returnRequiresInspection, returnInspectionBlocksSettlement, returnHasScheduledInspectionSettlement } from './ReturnInspectionPanel';
import { DefectReviewPanel } from './DefectReviewPanel';
import { DefectResolutionWorkflow } from './DefectResolutionWorkflow';
import { DefectResolutionPanel } from './DefectResolutionPanel';
import { ExternalLinkIcon, UserIcon } from '@heroicons/react/solid';
import { beginShopifyReauthorization } from 'utils/shopifyReauthorization';
import { openReturnLabel } from 'utils/openReturnLabel';
import { getSettlementActionState } from './returnSettlementAction';
import { getUnsupportedReturnResolutions } from './returnResolutionSupport';
import { parseReturnFeeAmount, roundReturnMoney } from 'utils/returnMoney';
import { hasValidReturnReceiptTimestamp } from 'utils/returnReceipt';
import { isMixedReturnCurrency, validateReturnMoneySnapshot } from 'utils/returnPresentmentMoney';
import { getNewReturnEvidence } from 'utils/newRequestPolicy';
import { NewReturnPolicy } from 'types/newRequest';
import { ReturnSettlementActionControl } from './ReturnSettlementActionControl';
import { ReturnStoreCreditActionControls } from './ReturnStoreCreditActionControls';
import NativeExchangeReview, { NativeExchangeStatus } from './NativeExchangeReview';
import ReturnCustomizationReview from './ReturnCustomizationReview';
import KeepItemCreditReview from './KeepItemCreditReview';
import {KeepItemCreditManagerStatus} from 'types/keepItemCredit';
import {keepItemCreditManaged, projectKeepItemCreditManagerStatus} from 'utils/keepItemCreditStatus';
import { getShopifyReturnSyncPresentation } from './shopifyReturnSyncPresentation';
import { TrackingLink } from 'components/TrackingLink';
import {
  getPersistedReturnLabelErrorPresentation,
  getReturnLabelErrorPresentation,
  ReturnLabelErrorPresentation,
} from './returnLabelErrorPresentation';

import { format } from 'date-fns';
import {
  PolicyStrictnessMode,
  POLICY_MODE_LABELS,
  ReturnApprovalMode,
  getPolicyModeBehavior,
} from 'types/policy';

const businessStatusToType = (
  businessStatus?: string | null,
): 'success' | 'info' | 'warning' | 'error' => {
  switch (businessStatus) {
    case 'APPROVED_BY_MERCHANT':
    case 'ITEM_RECEIVED_BACK_AT_WAREHOUSE':
    case 'REFUND_PROCESSED':
    case 'STORE_CREDIT_PROCESSED':
    case 'RETURN_RESOLVED':
      return 'success';
    case 'DENIED_BY_MERCHANT':
    case 'RETURN_EXPIRED':
      return 'error';
    case 'AWAITING_CUSTOMER_SHIPMENT':
    case 'RETURN_IN_TRANSIT':
      return 'warning';
    case 'RETURN_FILED':
    default:
      return 'info';
  }
};

const LABEL_SOURCE_LABELS: Record<string, string> = {
  EASYSHIP_SHIPAID_RATES: 'ShipAid Easyship rates',
  EASYSHIP_MERCHANT_CARRIER_ACCOUNT: 'Merchant connected carrier account',
  EASYSHIP_SHIPAID_RATES_PENDING: 'label generation in progress / locked',
};

const SHIPPING_OPTION_SOURCE_LABELS: Record<string, string> = {
  SHIPAID_RATES: 'ShipAid Easyship rates',
  MERCHANT_CARRIER_ACCOUNT: 'Merchant carrier account',
};

const SHOPIFY_RETURN_SYNC_STATUS_CLASSES: Record<string, string> = {
  SYNCED: 'border-green-200 bg-green-50 text-green-700',
  CREATED: 'border-green-200 bg-green-50 text-green-700',
  PENDING: 'border-blue-200 bg-blue-50 text-blue-700',
  RETRYING: 'border-blue-200 bg-blue-50 text-blue-700',
  WAITING_FOR_APPROVAL: 'border-gray-200 bg-gray-50 text-gray-700',
  NOT_REQUIRED: 'border-gray-200 bg-gray-50 text-gray-700',
  NEEDS_ATTENTION: 'border-amber-200 bg-amber-50 text-amber-800',
  SKIPPED: 'border-amber-200 bg-amber-50 text-amber-800',
  FAILED: 'border-red-200 bg-red-50 text-red-700',
};

const ERROR_CONNECTION_STATUSES = ['ERROR', 'FAILED', 'NEEDS_RECONNECT'];

const isShopifyAuthorizationErrorMessage = (message?: string | null) => {
  if (!message) return false;
  return /write_store_credit|reconnect shopify|shopify authorization|authorization scope|missing scope/i.test(
    message,
  );
};

const getShopifyReturnSyncErrorMessage = (message?: string | null) => {
  if (!message) return '';
  if (/Shopify line item .* is not currently returnable\.?/i.test(message)) {
    return 'Shopify has no returnable fulfilled quantity for this item. It may be unfulfilled, already fully returned, or otherwise ineligible. Check its fulfillment and existing returns in Shopify, then retry.';
  }
  return message;
};

const formatWorkflowLabel = (value?: string | null) => {
  if (!value) return 'N/A';
  return formatStatusText(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const RETURN_REASON_LABELS: Record<string, string> = {
  WRONG_ITEM: 'Wrong item',
  DAMAGED_OR_DEFECTIVE: 'Damaged or defective',
  CHANGED_MIND: 'Changed mind',
  MISSING_ITEM_OR_PARTS: 'Missing item or parts',
  NOT_AS_DESCRIBED: 'Not as described',
};

const formatReturnReason = (value?: string | null) => {
  if (!value) return 'No reason provided';
  const normalizedValue = value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (RETURN_REASON_LABELS[normalizedValue]) {
    return RETURN_REASON_LABELS[normalizedValue];
  }

  const formattedValue = formatStatusText(value).toLowerCase();
  return formattedValue.charAt(0).toUpperCase() + formattedValue.slice(1);
};

const formatWorkflowDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return format(date, 'do MMM yyyy hh:mm');
};

const getDateTimeMs = (value?: string | null) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const nativeReceiptTimestamp = (value: unknown): number | null => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const parsed = DateTime.fromISO(value, {setZone: true});
  return parsed.isValid ? parsed.toMillis() : null;
};

const hasCompleteReturnLabel = (
  returnRecord?: {
    labelUrl?: string | null;
    shippingId?: string | null;
    trackingNumber?: string | null;
  } | null,
) =>
  Boolean(
    returnRecord?.labelUrl &&
      returnRecord?.shippingId &&
      returnRecord?.trackingNumber,
  );

const hasAnyReturnLabelMetadata = (
  returnRecord?: {
    labelUrl?: string | null;
    shippingId?: string | null;
    trackingNumber?: string | null;
  } | null,
) =>
  Boolean(
    returnRecord?.labelUrl ||
      returnRecord?.shippingId ||
      returnRecord?.trackingNumber,
  );

const getPartialReturnLabelPresentation = (
  returnRecord?: {
    labelUrl?: string | null;
    shippingId?: string | null;
    trackingNumber?: string | null;
  } | null,
  supportReference?: string | null,
) =>
  getReturnLabelErrorPresentation({
    graphQLErrors: [
      {
        extensions: {
          code: 'RETURN_LABEL_PARTIAL_CREATED',
          category: 'PARTIAL_CREATED',
          action: 'WAIT_FOR_RECONCILIATION',
          outcome: returnRecord?.labelUrl
            ? 'LABEL_CREATED'
            : 'SHIPMENT_CREATED',
          retryDisposition: 'RECONCILE',
          reconnectRequired: false,
          supportReference,
        },
      },
    ],
  });

const getLegacyPendingReturnLabelPresentation = () =>
  getReturnLabelErrorPresentation({
    graphQLErrors: [
      {
        extensions: {
          code: 'RETURN_LABEL_GENERATION_IN_PROGRESS',
          category: 'IN_PROGRESS',
          action: 'WAIT_FOR_RECONCILIATION',
          outcome: 'UNKNOWN',
          retryDisposition: 'DO_NOT_RETRY',
          reconnectRequired: false,
        },
      },
    ],
  });

const getReviewSignalClasses = (severity?: string | null) => {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL':
    case 'HIGH':
    case 'ERROR':
      return 'border-red-200 bg-red-50 text-red-700';
    case 'MEDIUM':
    case 'WARNING':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    case 'LOW':
    case 'INFO':
      return 'border-blue-200 bg-blue-50 text-blue-700';
    default:
      return 'border-gray-200 bg-gray-50 text-gray-700';
  }
};

const getRuleFlagLabel = (flag: ReturnDashboardFlag | string) => {
  if (typeof flag === 'string') return formatWorkflowLabel(flag);
  return flag.label || formatWorkflowLabel(flag.code) || 'Flagged';
};

const getRuleFlagTitle = (flag: ReturnDashboardFlag | string) => {
  if (typeof flag === 'string') return formatWorkflowLabel(flag);
  return flag.message || flag.label || formatWorkflowLabel(flag.code);
};

const NEXT_ACTION_LABELS: Record<string, string> = {
  REVIEW_RETURN: 'Review request',
  CREATE_LABEL: 'Create return label',
  AWAITING_DROPOFF: 'Wait for customer drop-off',
  TRACK_RETURN: 'Track return shipment',
  INSPECT_RETURN: 'Inspect received items',
  CONFIRM_WAREHOUSE_RECEIPT: 'Confirm warehouse receipt',
  REVIEW_INSPECTION: 'Review inspection details',
  ISSUE_REFUND: 'Issue refund',
  ISSUE_STORE_CREDIT: 'Issue store credit',
  ISSUE_DISCOUNT_CODE: 'Create discount code',
  WAIT_FOR_REFUND_DUE: 'Wait for refund date',
  REVIEW_SETTLEMENT: 'Review settlement',
  SETTLEMENT_IN_PROGRESS: 'Settlement in progress',
  VERIFY_SETTLEMENT: 'Verify settlement in Shopify',
  AWAITING_RESOLUTION_RESPONSE: 'Await customer response',
  REVIEW_DEFECT_RESOLUTION: 'Review product resolution',
  COMPLETE_EXTERNAL_RESOLUTION: 'Complete agreed resolution',
  AWAITING_CUSTOMER_INFORMATION: 'Await customer information',
  COMPLETE_RETURN_INSPECTION: 'Inspect returned items',
  NONE: 'No action needed',
};

const getNextActionLabel = (nextAction?: string | null) =>
  nextAction
    ? NEXT_ACTION_LABELS[nextAction] || formatWorkflowLabel(nextAction)
    : 'No action assigned';

const getNextActionDescription = (nextAction?: string | null) => {
  switch (nextAction) {
    case 'REVIEW_RETURN':
      return 'Review the request and decide whether it should continue.';
    case 'CREATE_LABEL':
      return 'Create the return label so the customer can ship items back.';
    case 'AWAITING_DROPOFF':
      return 'Waiting for the customer to hand the return to the carrier.';
    case 'TRACK_RETURN':
      return 'The package is moving inbound or waiting for delivery.';
    case 'INSPECT_RETURN':
      return 'Complete the required receipt inspection before issuing funds.';
    case 'CONFIRM_WAREHOUSE_RECEIPT':
      return 'Confirm the returned items reached the warehouse, then inspect them before settlement.';
    case 'REVIEW_INSPECTION':
      return 'Required inspection details are missing or invalid. Refresh this return before issuing funds.';
    case 'ISSUE_REFUND':
      return 'Return is ready for refund settlement.';
    case 'ISSUE_STORE_CREDIT':
      return 'Return is ready for store-credit settlement.';
    case 'ISSUE_DISCOUNT_CODE':
      return 'Return is ready for discount-code creation.';
    case 'WAIT_FOR_REFUND_DUE':
      return 'Settlement is scheduled and waiting for the configured due date.';
    case 'REVIEW_SETTLEMENT':
      return 'Review the failed or manual settlement before retrying.';
    case 'SETTLEMENT_IN_PROGRESS':
      return 'A refund or store-credit settlement is already processing. Wait for it to finish before trying again.';
    case 'VERIFY_SETTLEMENT':
      return 'Open the Shopify order and verify whether the settlement completed. Do not retry this action. After verifying the Shopify outcome, contact ShipAid support to reconcile this return.';
    case 'AWAITING_RESOLUTION_RESPONSE':
      return 'The customer must accept or decline the proposed resolution before your team can continue.';
    case 'REVIEW_DEFECT_RESOLUTION':
      return 'Review the evidence and current customer response, then choose the next product resolution.';
    case 'COMPLETE_EXTERNAL_RESOLUTION':
      return 'Complete the agreed external action and record the actual outcome in the resolution workspace.';
    case 'AWAITING_CUSTOMER_INFORMATION':
      return 'Wait for the customer to answer the open information request before reviewing the resolution.';
    case 'COMPLETE_RETURN_INSPECTION':
      return 'Record the required item inspection before completing the agreed resolution.';
    case 'NONE':
      return 'No merchant action is currently required.';
    default:
      return nextAction
        ? 'Use the workflow details and timeline to decide the next step.'
        : 'Backend has not assigned a workflow action yet.';
  }
};

const getWorkflowEventLabel = (eventType?: string | null) => {
  switch (eventType) {
    case 'REMINDER_SENT':
      return 'Shipment reminder sent';
    case 'RETURN_FLAGGED':
      return 'Return flagged for review';
    case 'RETURN_FLAG_RESOLVED':
      return 'Return flag resolved';
    default:
      return formatWorkflowLabel(eventType);
  }
};

const getEventPayloadSummary = (
  eventType: ReturnEvent['type'],
  payload: ReturnEvent['payload'],
) => {
  if (payload === null || payload === undefined) return '';
  if (typeof payload !== 'object') return String(payload);
  if (eventType === 'REMINDER_SENT') {
    const parts = ['Shipment reminder sent'];
    if (payload.reminderDay !== undefined && payload.reminderDay !== null) {
      parts.push(`for reminder day ${payload.reminderDay}`);
    }
    if (
      payload.daysUntilDeadline !== undefined &&
      payload.daysUntilDeadline !== null
    ) {
      parts.push(
        `${payload.daysUntilDeadline} day${
          Number(payload.daysUntilDeadline) === 1 ? '' : 's'
        } until the ship-back deadline`,
      );
    }
    if (payload.shipmentBackDeadlineAt) {
      parts.push(
        `deadline ${formatWorkflowDate(payload.shipmentBackDeadlineAt)}`,
      );
    }
    if (payload.trackingNumber) {
      parts.push(`tracking ${payload.trackingNumber}`);
    }
    if (payload.status) {
      parts.push(`status ${formatWorkflowLabel(payload.status)}`);
    }
    return `${parts.join('. ')}.`;
  }
  if (eventType === 'RETURN_FLAGGED') {
    const parts = ['Return flagged for review'];
    const reviewReasons = Array.isArray(payload.reviewReasons)
      ? payload.reviewReasons.filter(Boolean)
      : [];
    const ruleFlags = Array.isArray(payload.ruleFlags)
      ? payload.ruleFlags.filter(Boolean)
      : [];

    if (payload.riskLevel) {
      parts.push(`risk ${formatWorkflowLabel(payload.riskLevel)}`);
    }
    if (reviewReasons.length) {
      parts.push(
        `reasons ${reviewReasons
          .map((reason) => formatWorkflowLabel(String(reason)))
          .join(', ')}`,
      );
    }
    if (ruleFlags.length) {
      parts.push(
        `rule flags ${ruleFlags
          .map((flag) => getRuleFlagLabel(flag))
          .join(', ')}`,
      );
    }
    if (payload.status) {
      parts.push(`status ${formatWorkflowLabel(payload.status)}`);
    }
    return `${parts.join('. ')}.`;
  }
  if (eventType === 'RETURN_FLAG_RESOLVED') {
    const parts = ['Return flag resolved'];
    const remainingRuleFlags = Array.isArray(payload.remainingRuleFlags)
      ? payload.remainingRuleFlags.filter(Boolean)
      : [];

    if (payload.resolvedFlag) {
      parts.push(`flag ${getRuleFlagLabel(payload.resolvedFlag)}`);
    } else if (payload.code) {
      parts.push(`flag ${formatWorkflowLabel(payload.code)}`);
    }
    if (payload.resolvedBy) {
      parts.push(`resolved by ${formatWorkflowLabel(payload.resolvedBy)}`);
    }
    if (payload.riskLevel) {
      parts.push(`risk ${formatWorkflowLabel(payload.riskLevel)}`);
    }
    if (payload.reviewRequired === false) {
      parts.push('review no longer required');
    }
    if (remainingRuleFlags.length) {
      parts.push(
        `remaining flags ${remainingRuleFlags
          .map((flag) => getRuleFlagLabel(flag))
          .join(', ')}`,
      );
    }
    return `${parts.join('. ')}.`;
  }
  const message = payload.message || payload.description || payload.reason;
  if (message) return String(message);
  return Object.entries(payload)
    .filter(
      ([key, value]) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !['number', 'status'].includes(key),
    )
    .map(([key, value]) => {
      const label = formatWorkflowLabel(
        key.replace(/([a-z0-9])([A-Z])/g, '$1_$2'),
      );
      const displayValue = /At$/.test(key)
        ? formatWorkflowDate(String(value))
        : formatWorkflowLabel(String(value));
      return `${label}: ${displayValue}`;
    })
    .join(' · ');
};

function nativeIncentiveMarker(record: Return | undefined) {
  const marker = record?.meta?.exchangeIncentive;
  if (!marker || typeof marker !== 'object' || Array.isArray(marker) || Object.keys(marker).length !== 4 || marker.version !== 1 ||
    typeof marker.operationId !== 'string' || !/^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/.test(marker.operationId) ||
    typeof marker.returnRequired !== 'boolean' || !['EXCHANGE', 'STORE_CREDIT_ALTERNATIVE'].includes(marker.outcome)) return null;
  const resolution = marker.outcome === 'STORE_CREDIT_ALTERNATIVE' ? 'STORE_CREDIT' : 'EXCHANGE';
  if (record?.requestedResolution !== resolution || record.returnItems.length !== 1 || record.returnItems[0].requestedResolution !== resolution ||
    (record.returnItems[0].resolution && record.returnItems[0].resolution !== resolution) ||
    (marker.outcome === 'STORE_CREDIT_ALTERNATIVE' && marker.returnRequired !== true)) return null;
  return marker;
}

export const ReturnDetail = withNotification(({ showNotification }: any) => {
  const { returnId } = useParams();
  const location = useLocation();
  const returnTo = getRequestReturnTo(
    location.search,
    (location.state as { returnLocation?: string } | null)?.returnLocation,
    '/returns',
  );
  const { storeId, storeProperties, storeDomain } = useStore();
  const nativeScopeKey = JSON.stringify([storeId, returnId]);
  const nativeOwner = useRef({key: nativeScopeKey});
  if (nativeOwner.current.key !== nativeScopeKey) nativeOwner.current = {key: nativeScopeKey};
  const nativeScope = nativeOwner.current;
  const [instructionRefreshRevision, setInstructionRefreshRevision] = useState(0);
  const [nativeStatusState, setNativeStatusState] = useState<{scope: object; status: NativeExchangeStatus | null} | null>(null);
  const [keepCreditStatusState, setKeepCreditStatusState] = useState<{scope: object; status: KeepItemCreditManagerStatus | null} | null>(null);
  const [nativeLoadingState, setNativeLoadingState] = useState<{scope: object; loading: boolean} | null>(null);
  const nativeStatusLoading = nativeLoadingState?.scope === nativeScope ? nativeLoadingState.loading : true;
  const nativeStatusCandidate = nativeStatusState?.scope === nativeScope ? nativeStatusState.status : null;
  const onNativeStatusChange = useCallback((status: NativeExchangeStatus | null) => {
    if (nativeOwner.current === nativeScope) setNativeStatusState({scope: nativeScope, status});
  }, [nativeScope]);
  const onNativeLoadingChange = useCallback((loading: boolean) => {
    if (nativeOwner.current === nativeScope) setNativeLoadingState({scope: nativeScope, loading});
  }, [nativeScope]);
  const onKeepCreditStatusChange = useCallback((status: KeepItemCreditManagerStatus | null) => {
    if (nativeOwner.current === nativeScope) setKeepCreditStatusState({scope: nativeScope, status});
  }, [nativeScope]);
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [warehouseEditing, setWarehouseEditing] = useState(false);
  const [reviewedDefectVersion, setReviewedDefectVersion] = useState<
    string | null
  >(null);
  const [defectWorkflow, setDefectWorkflow] = useState({ managed: false, returnShippingAllowed: false });
  const [resolvingRuleFlagCode, setResolvingRuleFlagCode] = useState<
    string | null
  >(null);
  const resolvingRuleFlagRef = useRef(false);
  const [labelErrorPresentation, setLabelErrorPresentation] =
    useState<ReturnLabelErrorPresentation | null>(null);
  const [labelRetryReady, setLabelRetryReady] = useState(false);
  const [labelPollingExhausted, setLabelPollingExhausted] = useState(false);
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;
  const [showShopifyReconnectCta, setShowShopifyReconnectCta] = useState(false);

  useEffect(() => {
    setApprovalModalOpen(false);
    setWarehouseEditing(false);
    setLabelErrorPresentation(null);
    setLabelRetryReady(false);
    setLabelPollingExhausted(false);
    setDefectWorkflow({ managed: false, returnShippingAllowed: false });
  }, [returnId]);

  const { data, loading, refetch } = useQuery(GET_RETURN, {
    variables: {
      storeId,
      id: returnId,
    },
    onError(error) {
      showNotification(NotificationTypes.error, error?.message);
    },
  });
  const queriedReturnData: Return | undefined = data?.returnRecord;
  const nativeIncentiveManaged = Boolean(queriedReturnData?.meta && Object.prototype.hasOwnProperty.call(queriedReturnData.meta, 'exchangeIncentive'));
  const keepCreditManaged = keepItemCreditManaged(queriedReturnData);
  const keepCreditStatus = keepCreditStatusState?.scope === nativeScope
    ? projectKeepItemCreditManagerStatus(keepCreditStatusState.status, queriedReturnData, storeId || '', returnId || '') : null;
  const incentiveMarker = nativeIncentiveMarker(queriedReturnData);
  const nativeExchangeStatus = !keepCreditManaged && nativeStatusCandidate && (!nativeIncentiveManaged || (incentiveMarker &&
    nativeStatusCandidate.incentive && nativeStatusCandidate.incentive.outcome === incentiveMarker.outcome && nativeStatusCandidate.incentive.returnRequired === incentiveMarker.returnRequired)) ? nativeStatusCandidate : null;
  const nativeExchangeVerified = !!nativeExchangeStatus;
  const nativeAlternative = nativeExchangeStatus?.incentive?.outcome === 'STORE_CREDIT_ALTERNATIVE';
  const nativeApprovalNeeded = !!nativeExchangeStatus && !['READY', 'PROCESSED'].includes(nativeExchangeStatus.status) &&
    ((nativeExchangeStatus.approvalRequired && !nativeExchangeStatus.approvalRecorded) || nativeExchangeStatus.returnApproved === false);
  const nativeShippingBlocked = keepCreditManaged || nativeExchangeStatus?.incentive?.returnRequired === false || (nativeIncentiveManaged && !nativeExchangeVerified);
  const returnCurrency = [queriedReturnData?.order.paymentCurrencyCode, queriedReturnData?.order.currencyCode, storeProperties?.currency]
    .find((value) => typeof value === 'string' && value.trim())?.trim().toUpperCase() || '';
  const inspectionSettlementBlocked = returnInspectionBlocksSettlement(queriedReturnData, returnCurrency);
  const savedFees = queriedReturnData?.policySnapshot?.dynamicFees;
  const savedAmounts = [
    ...(Array.isArray(savedFees) ? savedFees : [])
      .filter((fee) => fee.calculatedAmount != null)
      .map((fee) => parseReturnFeeAmount(fee.calculatedAmount)),
    ...[
      queriedReturnData?.meta?.inspection?.requestedAmount,
      queriedReturnData?.meta?.inspection?.approvedAmount,
    ].filter((amount) => amount != null),
  ];
  const currencyPrecisionReviewRequired = savedAmounts.some(
    (amount) =>
      typeof amount !== 'number' ||
      !Number.isFinite(amount) ||
      amount < 0 ||
      roundReturnMoney(amount, returnCurrency) !== amount,
  );
  const presentmentMoneyUnavailable = Boolean(
    queriedReturnData && (
      queriedReturnData.returnItems.some((item) => !item.orderItem) ||
      (isMixedReturnCurrency(queriedReturnData.order, storeProperties?.currency) &&
        !validateReturnMoneySnapshot({
          ...queriedReturnData.order,
          id: queriedReturnData.orderId,
          items: queriedReturnData.returnItems.map((item) => item.orderItem),
        }, queriedReturnData.policySnapshot?.returnMoney, false, storeProperties?.currency))
    ),
  );
  const financialReviewRequired = currencyPrecisionReviewRequired || presentmentMoneyUnavailable;
  const storeCreditRequested = [queriedReturnData?.requestedResolution, queriedReturnData?.deflectionOption]
    .some((value) => ['STORE_CREDIT', 'STORE_CREDIT_BONUS'].includes(String(value || '').trim().toUpperCase()));
  const unsupportedResolutions = getUnsupportedReturnResolutions(queriedReturnData);
  const resolutionProcessingUnavailable = unsupportedResolutions.length > 0 || nativeIncentiveManaged;
  const nativeReviewCandidate = !keepCreditManaged && ((unsupportedResolutions.length === 1 && unsupportedResolutions[0] === 'EXCHANGE') ||
    Boolean(incentiveMarker && queriedReturnData && queriedReturnData.id === returnId && queriedReturnData.storeId === storeId));
  const defectReview = queriedReturnData?.meta?.defectReview;
  const isDefectReturn = Boolean(
    queriedReturnData?.policySnapshot?.defectReviewRequired === true ||
      (defectReview &&
        typeof defectReview === 'object' &&
        !Array.isArray(defectReview)),
  );
  const defectResolutionManaged = isDefectReturn && !nativeExchangeVerified && (defectWorkflow.managed ||
    storeProperties?.policySettings?.defectResolutionPolicy?.enabled === true ||
    Boolean(queriedReturnData?.meta?.defectResolution));
  const defectPlanShippingBlocked = nativeShippingBlocked || (defectResolutionManaged && !defectWorkflow.returnShippingAllowed);
  const warehouseApprovalBlocked = !defectResolutionManaged && (warehouseEditing || returnWarehouseNeedsStaffAction(queriedReturnData));
  const snapshotEvidence = queriedReturnData?.policySnapshot?.evidenceRequirements as NewReturnPolicy['evidenceRequirements'] | undefined;
  const evidenceByItem = (queriedReturnData?.returnItems || []).map((item) => getNewReturnEvidence({ evidenceRequirements: snapshotEvidence } as NewReturnPolicy, item.reason || ''));
  const defectPhotosRequired = !snapshotEvidence || !evidenceByItem.length || evidenceByItem.some((evidence) => evidence.photos === 'REQUIRED') || !evidenceByItem.some((evidence) => evidence.defect || ['OPTIONAL', 'HIDDEN'].includes(evidence.photos));
  const defectReviewDetailsReady = Boolean(
    defectReview &&
      defectReview.version === 1 &&
      ['PENDING', 'APPROVED', 'DENIED', 'MORE_INFORMATION_REQUESTED'].includes(defectReview.status) &&
      Number.isInteger(defectReview.evidenceRevision) &&
      defectReview.evidenceRevision > 0 &&
      queriedReturnData?.updatedAt,
  );
  const defectReviewApproved =
    isDefectReturn &&
    defectReviewDetailsReady &&
    defectReview?.status === 'APPROVED';
  const defectReviewBlocked = isDefectReturn && !defectReviewApproved;
  const defectRequestClosed =
    isDefectReturn &&
    [ReturnStatus.RESOLVED, ReturnStatus.DENIED, ReturnStatus.EXPIRED].includes(
      queriedReturnData?.status as ReturnStatus,
    );
  const defectEvidenceVersion = `${queriedReturnData?.id}:${queriedReturnData?.updatedAt}:${defectReview?.evidenceRevision}`;
  const defectEvidenceAcknowledged = reviewedDefectVersion === defectEvidenceVersion;
  const defectDecisionVersions = isDefectReturn
    ? {
        expectedUpdatedAt: queriedReturnData?.updatedAt,
        expectedEvidenceRevision: defectReview?.evidenceRevision,
      }
    : {};
  const selectedShippingOptionSource =
    queriedReturnData?.selectedShippingOption?.source;
  const returnLabelSource = (storeProperties?.features as any)?.returns
    ?.returnLabels?.source;
  const isMerchantCarrierLabelSource =
    selectedShippingOptionSource === 'MERCHANT_CARRIER_ACCOUNT' ||
    (!selectedShippingOptionSource &&
      returnLabelSource === 'MERCHANT_CARRIER_ACCOUNT');
  const persistedLabelErrorPresentation = useMemo(
    () => getPersistedReturnLabelErrorPresentation(queriedReturnData?.meta),
    [queriedReturnData?.meta],
  );
  const {
    data: easyshipConnectionData,
    loading: easyshipConnectionLoading,
    error: easyshipConnectionQueryError,
    refetch: refetchEasyshipConnection,
  } = useQuery(GET_EASYSHIP_CONNECTION, {
    variables: { input: { storeId } },
    skip: !storeId || !isMerchantCarrierLabelSource,
    fetchPolicy: 'network-only',
  });
  const easyshipConnection =
    easyshipConnectionData?.getEasyshipConnection || null;
  const easyshipStatus = String(easyshipConnection?.status || '');
  const hasEasyshipConnectionError = ERROR_CONNECTION_STATUSES.includes(
    easyshipStatus.toUpperCase(),
  );
  const isEasyshipConnected =
    easyshipConnection?.connected === true && !hasEasyshipConnectionError;
  const partialLabelPresentation = useMemo(() => {
    const returnLabelMetadata = {
      labelUrl: queriedReturnData?.labelUrl,
      shippingId: queriedReturnData?.shippingId,
      trackingNumber: queriedReturnData?.trackingNumber,
    };

    return hasAnyReturnLabelMetadata(returnLabelMetadata) &&
      !hasCompleteReturnLabel(returnLabelMetadata)
      ? getPartialReturnLabelPresentation(returnLabelMetadata)
      : null;
  }, [
    queriedReturnData?.labelUrl,
    queriedReturnData?.shippingId,
    queriedReturnData?.trackingNumber,
  ]);
  const legacyPendingLabelPresentation = useMemo(
    () =>
      queriedReturnData?.labelSource === 'EASYSHIP_SHIPAID_RATES_PENDING'
        ? getLegacyPendingReturnLabelPresentation()
        : null,
    [queriedReturnData?.labelSource],
  );
  const persistedReauthorizationResolved = Boolean(
    persistedLabelErrorPresentation?.reconnectRequired &&
      persistedLabelErrorPresentation.persistedStatus === 'FAILED' &&
      persistedLabelErrorPresentation.lockRetained === false &&
      isEasyshipConnected,
  );
  const effectivePersistedLabelErrorPresentation =
    persistedReauthorizationResolved ? null : persistedLabelErrorPresentation;
  const existingPartialPresentation =
    labelErrorPresentation?.mayHaveCreatedLabel
      ? labelErrorPresentation
      : effectivePersistedLabelErrorPresentation?.mayHaveCreatedLabel
      ? effectivePersistedLabelErrorPresentation
      : partialLabelPresentation;
  const activeLabelErrorPresentation = hasCompleteReturnLabel(queriedReturnData)
    ? null
    : hasAnyReturnLabelMetadata(queriedReturnData)
    ? existingPartialPresentation
    : labelErrorPresentation ??
      effectivePersistedLabelErrorPresentation ??
      legacyPendingLabelPresentation;

  const [modifyStatus, { loading: updatingStatus }] = useMutation(
    MODIFY_RETURN_STATUS,
    {
      onCompleted: (result) => {
        const keepItemIncentiveIssued =
          queriedReturnData?.deflectionOption === 'KEEP_ITEM_DISCOUNT' &&
          result?.modifyReturnStatus?.status === ReturnStatus.RESOLVED;
        showNotification(
          NotificationTypes.success,
          keepItemIncentiveIssued
            ? 'Return approved and incentive issued!'
            : 'Return status updated',
        );
        setDenyModalOpen(false);
        refetch();
      },
      onError: (error) => {
        const newError = apiErrorHandler(error);
        if (isShopifyAuthorizationErrorMessage(newError?.message)) {
          setShowShopifyReconnectCta(true);
        }
        showNotification(NotificationTypes.error, newError?.message);
        refetch();
      },
    },
  );

  const [createLabel, { loading: creatingLabel }] = useMutation(
    CREATE_LABEL_FOR_RETURN,
    {
      onCompleted: () => {
        setLabelErrorPresentation(null);
        setLabelRetryReady(false);
        setLabelPollingExhausted(false);
        showNotification(
          NotificationTypes.success,
          "Label created successfully! Note: this label hasn't been sent to the customer automatically.",
        );
        refetch();
      },
      onError: async (error) => {
        const newError = apiErrorHandler(error);
        const presentation = getReturnLabelErrorPresentation(newError, {
          allowLegacyAuthFallback: isMerchantCarrierLabelSource,
        });

        try {
          const refreshed = await refetch();
          const refreshedReturn: Return | undefined =
            refreshed?.data?.returnRecord;
          if (hasCompleteReturnLabel(refreshedReturn)) {
            setLabelErrorPresentation(null);
            setLabelRetryReady(false);
            setLabelPollingExhausted(false);
            showNotification(
              NotificationTypes.success,
              "Label created successfully! Note: this label hasn't been sent to the customer automatically.",
            );
            return;
          }
          if (hasAnyReturnLabelMetadata(refreshedReturn)) {
            const partialPresentation = getPartialReturnLabelPresentation(
              refreshedReturn,
              presentation.supportReference,
            );
            setLabelRetryReady(false);
            setLabelPollingExhausted(false);
            setLabelErrorPresentation(partialPresentation);
            showNotification(
              NotificationTypes.pending,
              partialPresentation.message,
            );
            return;
          }
        } catch (_refetchError) {
          // The safe mutation error guidance below remains actionable when refresh fails.
        }

        setLabelRetryReady(false);
        setLabelPollingExhausted(false);
        setLabelErrorPresentation(presentation);
        showNotification(
          presentation.shouldPoll
            ? NotificationTypes.pending
            : NotificationTypes.error,
          presentation.message,
        );
      },
    },
  );

  const [sendReturnLabelToCustomer, { loading: sendingReturnLabel }] =
    useMutation(SEND_RETURN_LABEL_TO_CUSTOMER, {
      onCompleted: () => {
        showNotification(
          NotificationTypes.success,
          'Return label sent to the customer.',
        );
      },
      onError: (error) => {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

  const [createRefund, { loading: refunding }] = useMutation(
    CREATE_RETURN_REFUND,
    {
      onCompleted: (result) => {
        const settlement = result?.createReturnRefund;
        if (settlement?.settlementStatus !== 'COMPLETED') {
          showNotification(
            NotificationTypes.error,
            settlement?.settlementFailureReason ||
              'Refund could not be processed. Review the settlement status and retry.',
          );
          refetch();
          return;
        }
        showNotification(
          NotificationTypes.success,
          'Refund processed successfully!',
        );
        refetch();
      },
      onError: (error) => {
        const newError = apiErrorHandler(error);
        if (isShopifyAuthorizationErrorMessage(newError?.message)) {
          setShowShopifyReconnectCta(true);
        }
        showNotification(NotificationTypes.error, newError?.message);
        refetch();
      },
    },
  );

  const [resolveDeflection, { loading: resolvingDeflection }] = useMutation(
    RESOLVE_RETURN_WITH_DEFLECTION,
    {
      onCompleted: () => {
        showNotification(
          NotificationTypes.success,
          'Return resolved successfully!',
        );
        refetch();
      },
      onError: (error) => {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
        refetch();
      },
    },
  );

  const [createStoreCredit, { loading: creatingStoreCredit }] = useMutation(
    CREATE_STORE_CREDIT,
    {
      onCompleted: (result) => {
        const settlement = result?.createStoreCredit;
        if (settlement?.settlementStatus !== 'COMPLETED') {
          showNotification(
            NotificationTypes.error,
            settlement?.settlementFailureReason ||
              'Store credit could not be issued. Review the settlement status and retry.',
          );
          refetch();
          return;
        }
        showNotification(
          NotificationTypes.success,
          'Store credit issued successfully!',
        );
        refetch();
      },
      onError: (error) => {
        const newError = apiErrorHandler(error);
        if (isShopifyAuthorizationErrorMessage(newError?.message)) {
          setShowShopifyReconnectCta(true);
        }
        showNotification(NotificationTypes.error, newError?.message);
        refetch();
      },
    },
  );

  const [generateInstallLink, { loading: reauthorizingShopify }] = useMutation(
    GENERATE_INSTALL_LINK,
  );

  const [markAsReceived, { loading: markingAsReceived }] = useMutation(
    MARK_RETURN_AS_RECEIVED,
    {
      onCompleted: () => {
        showNotification(
          NotificationTypes.success,
          'Return marked as received successfully!',
        );
        refetch();
      },
      onError: (error) => {
        const code = error?.graphQLErrors?.[0]?.extensions?.code;
        const rejection = {
          'invalid-jwt': '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED INVALID_JWT',
          'access-denied': '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED ACCESS_DENIED',
          'validation-failed': '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED VALIDATION_FAILED',
          'parse-failed': '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED PARSE_FAILED',
          'remote-schema-error': '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED REMOTE_SCHEMA_ERROR',
          unexpected: '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED UNEXPECTED',
          INTERNAL_SERVER_ERROR: '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED INTERNAL_SERVER_ERROR',
          BAD_USER_INPUT: '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED BAD_USER_INPUT',
          'permission-denied': '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED PERMISSION_DENIED',
          GRAPHQL_VALIDATION_FAILED: '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED GRAPHQL_VALIDATION_FAILED',
          'constraint-violation': '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED CONSTRAINT_VIOLATION',
          FORBIDDEN: '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED FORBIDDEN',
          RETURN_UNSUPPORTED_RESOLUTION: '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED RETURN_UNSUPPORTED_RESOLUTION',
        };
        console.warn(typeof code === 'string' && Object.prototype.hasOwnProperty.call(rejection, code)
          ? rejection[code as keyof typeof rejection] : '[ReturnsReceipt] RECEIPT_MUTATION_REJECTED UNKNOWN');
        apiErrorHandler(error);
        showNotification(NotificationTypes.error, 'Could not confirm receipt. Refresh this request before trying again.');
      },
    },
  );

  const [resolveReturnFlag] = useMutation(RESOLVE_RETURN_FLAG);
  const [retryShopifyReturnSync, { loading: retryingShopifyReturnSync }] =
    useMutation(RETRY_SHOPIFY_RETURN_SYNC, {
      onCompleted: (result) => {
        const retryResult = result?.retryShopifyReturnSync;
        const retryStatus = String(retryResult?.shopifyReturnSyncStatus ?? '')
          .trim()
          .toUpperCase();

        if (
          retryResult?.shopifyReturnSyncError ||
          ['FAILED', 'SKIPPED'].includes(retryStatus)
        ) {
          showNotification(
            NotificationTypes.error,
            retryResult?.shopifyReturnSyncError ||
              'Shopify return sync could not be completed.',
          );
        } else if (['PENDING', 'RETRYING'].includes(retryStatus)) {
          showNotification(
            NotificationTypes.pending,
            'Shopify return sync is still in progress.',
          );
        } else if (
          retryResult?.shopifyReturnId ||
          ['SYNCED', 'CREATED'].includes(retryStatus)
        ) {
          showNotification(
            NotificationTypes.success,
            'Shopify return sync completed.',
          );
        } else {
          showNotification(
            NotificationTypes.error,
            'Shopify return sync could not be confirmed. Refresh this return before retrying.',
          );
        }
        refetch();
      },
      onError: (error) => {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

  useEffect(() => {
    if (
      activeLabelErrorPresentation?.retryDisposition !== 'RETRY_LATER' ||
      activeLabelErrorPresentation.outcome !== 'NOT_CREATED' ||
      activeLabelErrorPresentation.mayHaveCreatedLabel
    ) {
      return;
    }

    setLabelRetryReady(false);
    const retryDelayMs =
      (activeLabelErrorPresentation.retryAfterSeconds ?? 60) * 1000;
    const retryTimer = window.setTimeout(
      () => setLabelRetryReady(true),
      retryDelayMs,
    );
    return () => window.clearTimeout(retryTimer);
  }, [activeLabelErrorPresentation]);

  useEffect(() => {
    if (!activeLabelErrorPresentation?.shouldPoll) return;

    let cancelled = false;
    let completedChecks = 0;
    let refreshTimer: number | undefined;
    setLabelPollingExhausted(false);

    const refreshForLabel = async () => {
      if (cancelled) return;
      completedChecks += 1;
      try {
        const refreshed = await refetch();
        if (cancelled) return;
        if (hasCompleteReturnLabel(refreshed?.data?.returnRecord)) {
          setLabelErrorPresentation(null);
          setLabelPollingExhausted(false);
          showNotificationRef.current(
            NotificationTypes.success,
            "Label created successfully! Note: this label hasn't been sent to the customer automatically.",
          );
          return;
        }
      } catch (_refetchError) {
        if (cancelled) return;
      }

      if (completedChecks >= 3) {
        setLabelPollingExhausted(true);
        return;
      }
      refreshTimer = window.setTimeout(refreshForLabel, 5000);
    };

    refreshTimer = window.setTimeout(refreshForLabel, 5000);
    return () => {
      cancelled = true;
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
    };
  }, [
    activeLabelErrorPresentation?.code,
    activeLabelErrorPresentation?.outcome,
    activeLabelErrorPresentation?.shouldPoll,
    activeLabelErrorPresentation?.supportReference,
    refetch,
    returnId,
  ]);

  const refreshLabelReadiness = async () => {
    try {
      const refreshed = await refetch();
      const refreshedReturn: Return | undefined = refreshed?.data?.returnRecord;
      if (hasCompleteReturnLabel(refreshedReturn)) {
        setLabelErrorPresentation(null);
        showNotification(
          NotificationTypes.success,
          "Label created successfully! Note: this label hasn't been sent to the customer automatically.",
        );
        return;
      }

      const persistedPresentation = getPersistedReturnLabelErrorPresentation(
        refreshedReturn?.meta,
      );
      if (persistedPresentation) {
        setLabelErrorPresentation(persistedPresentation);
        return;
      }
      if (
        refreshedReturn?.status === ReturnStatus.APPROVED &&
        refreshedReturn.labelSource !== 'EASYSHIP_SHIPAID_RATES_PENDING'
      ) {
        setLabelErrorPresentation(null);
      }
    } catch (_refetchError) {
      showNotification(
        NotificationTypes.error,
        'Could not refresh this return. Try again shortly.',
      );
    }
  };

  const submitApproval = (expectedUpdatedAt?: string) => {
    if (resolutionProcessingUnavailable || warehouseApprovalBlocked) return;
    if (
      isDefectReturn &&
      (!defectReviewDetailsReady || !defectEvidenceAcknowledged)
    )
      return;
    modifyStatus({
      variables: {
        input: {
          id: returnId,
          status: ReturnStatus.APPROVED,
          storeId,
          ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
          ...defectDecisionVersions,
        },
      },
    });
  };

  const handleApprove = () => {
    if (resolutionProcessingUnavailable || warehouseApprovalBlocked) return;
    if (isDefectReturn) {
      submitApproval();
      return;
    }
    setApprovalModalOpen(true);
  };

  const confirmApproval = (expectedUpdatedAt: string) => {
    if (queriedReturnData?.updatedAt !== expectedUpdatedAt) {
      setApprovalModalOpen(false);
      refetch();
      showNotification(
        NotificationTypes.error,
        'This return changed. Review its current details before approving.',
      );
      return;
    }
    setApprovalModalOpen(false);
    submitApproval(expectedUpdatedAt);
  };

  const handleDeny = (input: { reason: string; description: string }) => {
    if (keepCreditManaged) return;
    if (
      isDefectReturn &&
      (!defectReviewDetailsReady ||
        (defectReviewBlocked && !defectEvidenceAcknowledged))
    ) {
      setDenyModalOpen(false);
      showNotification(
        NotificationTypes.error,
        'The request changed. Review the current evidence before deciding.',
      );
      return;
    }
    modifyStatus({
      variables: {
        input: {
          id: returnId,
          status: ReturnStatus.DENIED,
          storeId,
          denialReason: input.reason,
          denialDescription: input.description,
          ...defectDecisionVersions,
        },
      },
    });
  };

  const handleRefund = () => {
    if (!returnId || !storeId || resolutionProcessingUnavailable || inspectionSettlementBlocked || financialReviewRequired || defectResolutionManaged) return;

    createRefund({
      variables: {
        input: {
          returnId,
          storeId,
        },
      },
    });
  };

  const handleResolveDeflection = () => {
    if (!returnId || !storeId || resolutionProcessingUnavailable || financialReviewRequired) return;

    resolveDeflection({
      variables: {
        input: {
          returnId,
          storeId,
        },
      },
    });
  };

  const handleStoreCredit = () => {
    if (!returnId || !storeId || resolutionProcessingUnavailable || inspectionSettlementBlocked || financialReviewRequired || defectResolutionManaged) return;

    createStoreCredit({
      variables: {
        input: {
          returnId,
          storeId,
        },
      },
    });
  };

  const handleOpenReturnLabel = () => {
    if (!queriedReturnData?.labelUrl) return;

    try {
      openReturnLabel(queriedReturnData.labelUrl);
    } catch (error) {
      showNotification(
        NotificationTypes.error,
        error instanceof Error
          ? error.message
          : 'The return label could not be opened. Please contact ShipAid support.',
      );
    }
  };

  const handleSendReturnLabel = () => {
    if (!returnId || !storeId || resolutionProcessingUnavailable) return;

    sendReturnLabelToCustomer({
      variables: {
        input: {
          returnId,
          storeId,
        },
      },
    });
  };

  const handleReconnectShopify = async () => {
    if (!storeDomain) {
      showNotification(
        NotificationTypes.error,
        'Select a Shopify store before reconnecting.',
      );
      return;
    }

    try {
      const { data: installLinkData } = await generateInstallLink({
        variables: { store: storeDomain },
      });
      beginShopifyReauthorization(installLinkData?.generateInstallLink);
    } catch (error) {
      const newError = apiErrorHandler(error as any);
      showNotification(
        NotificationTypes.error,
        newError?.message || 'Could not start Shopify reauthorization.',
      );
    }
  };

  // Inspection needs receipt before its separate processing approval can be recorded.
  const inspectedNativeRequestApproved = Boolean(nativeExchangeStatus &&
    ['WAITING_TRIGGER', 'CREATED'].includes(nativeExchangeStatus.status) && !nativeExchangeStatus.requiresManualReview &&
    nativeExchangeStatus.requestApproval?.mode === 'AUTOMATIC' && nativeExchangeStatus.requestApproval.decision === 'AUTOMATIC' &&
    nativeExchangeStatus.inspectionRelease?.required === true && returnRequiresInspection(queriedReturnData));
  const verifiedNativeReceipt = Boolean(nativeExchangeStatus && queriedReturnData &&
    queriedReturnData.id === returnId && queriedReturnData.storeId === storeId &&
    (incentiveMarker ? nativeExchangeStatus.incentive?.returnRequired === true :
      queriedReturnData.requestedResolution === 'EXCHANGE' &&
      queriedReturnData.returnItems.length === 1 && queriedReturnData.returnItems[0].requestedResolution === 'EXCHANGE' &&
      unsupportedResolutions.length === 1 && unsupportedResolutions[0] === 'EXCHANGE') &&
    nativeExchangeStatus.returnApproved === true &&
    (!nativeExchangeStatus.approvalRequired || nativeExchangeStatus.approvalRecorded || inspectedNativeRequestApproved));
  const receivedTimestamp = nativeReceiptTimestamp(queriedReturnData?.meta?.receivedAt);
  const receivedTimeValid = receivedTimestamp !== null && receivedTimestamp <= Date.now();
  const receiptEvents = queriedReturnData?.events;
  const receiptHistoryKnown = Array.isArray(receiptEvents) && receiptEvents.every(event => event && typeof event === 'object' && !Array.isArray(event) && typeof event.type === 'string' && event.type.length > 0);
  const warehouseReceiptEvents = receiptHistoryKnown ? receiptEvents!.filter(event => event.type === 'WAREHOUSE_RECEIVED') : [];
  const receiptProof = warehouseReceiptEvents[0];
  const exactReceiptProof = receivedTimeValid && warehouseReceiptEvents.length === 1 &&
    /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/.test(receiptProof.id) && receiptProof.source === 'MERCHANT' &&
    nativeReceiptTimestamp(receiptProof.occurredAt) === receivedTimestamp && receiptProof.payload && typeof receiptProof.payload === 'object' && !Array.isArray(receiptProof.payload) &&
    receiptProof.payload.status === 'RECEIVED' && nativeReceiptTimestamp(receiptProof.payload.receivedAt) === receivedTimestamp;
  const nativeReceiptRecorded = verifiedNativeReceipt && queriedReturnData?.status === ReturnStatus.RECEIVED;
  const retryReceiptConfirmation = nativeReceiptRecorded && receivedTimeValid && receiptHistoryKnown && warehouseReceiptEvents.length === 0;
  const receiptConfirmationNeedsReview = nativeReceiptRecorded && !retryReceiptConfirmation && !exactReceiptProof;
  const deliveryReceiptNeedsConfirmation = Boolean(queriedReturnData?.meta?.inspection?.deliveryReview) && (
    (queriedReturnData?.nextAction === 'CONFIRM_WAREHOUSE_RECEIPT' && !hasValidReturnReceiptTimestamp(queriedReturnData?.meta?.receivedAt)) ||
    queriedReturnData?.meta?.inspection?.warehouseConfirmation?.status === 'REQUESTED'
  );
  const receiptAvailable = !!returnId && !!storeId && !loading && !defectReviewBlocked && !defectPlanShippingBlocked && (
    (!resolutionProcessingUnavailable && queriedReturnData?.status === ReturnStatus.LABEL_READY) ||
    (!resolutionProcessingUnavailable && queriedReturnData?.status === ReturnStatus.RECEIVED && deliveryReceiptNeedsConfirmation) ||
    (verifiedNativeReceipt && [ReturnStatus.APPROVED, ReturnStatus.AWAITING_DROPOFF, ReturnStatus.LABEL_READY, ReturnStatus.IN_TRANSIT]
      .includes(queriedReturnData?.status as ReturnStatus)) || retryReceiptConfirmation);
  const receiptInspection = queriedReturnData?.meta?.inspection;
  const receiptInspectionPolicy = queriedReturnData?.policySnapshot?.inspection as any;
  const receiptUuid = (value: unknown) => typeof value === 'string' && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/.test(value) ? value : null;
  const receiptAuthorityKey = JSON.stringify({
    row: [queriedReturnData?.id, queriedReturnData?.storeId, queriedReturnData?.orderId, queriedReturnData?.updatedAt,
      queriedReturnData?.status, queriedReturnData?.requestedResolution, queriedReturnData?.deflectionOption, queriedReturnData?.reviewRequired],
    items: queriedReturnData?.returnItems.map(item => [item.id, item.orderItemId, item.quantity, item.requestedResolution, item.resolution]),
    marker: [keepCreditManaged, nativeIncentiveManaged, incentiveMarker, receiptUuid(queriedReturnData?.meta?.nativeExchangeOperationId)],
    inspection: [receiptInspectionPolicy?.enabled === true,
      ['ALWAYS', 'MATCHING', 'NEVER'].includes(receiptInspectionPolicy?.mode) ? receiptInspectionPolicy.mode : null,
      receiptInspectionPolicy?.policyEnabled === false, receiptInspection?.required === true,
      Number.isSafeInteger(receiptInspection?.version) ? receiptInspection.version : null,
      Number.isSafeInteger(receiptInspection?.revision) ? receiptInspection.revision : null,
      ['PENDING', 'APPROVED', 'REDUCED', 'DENIED'].includes(receiptInspection?.status) ? receiptInspection.status : null,
      nativeReceiptTimestamp(receiptInspection?.startedAt), nativeReceiptTimestamp(receiptInspection?.reviewedAt), receiptUuid(receiptInspection?.reviewedBy),
      typeof receiptInspection?.requestedAmount === 'number' && Number.isFinite(receiptInspection.requestedAmount) ? receiptInspection.requestedAmount : null,
      typeof receiptInspection?.approvedAmount === 'number' && Number.isFinite(receiptInspection.approvedAmount) ? receiptInspection.approvedAmount : null,
      typeof receiptInspection?.currency === 'string' && /^[A-Z]{3}$/.test(receiptInspection.currency) ? receiptInspection.currency : null,
      ['REQUESTED', 'CONFIRMED'].includes(receiptInspection?.warehouseConfirmation?.status) ? receiptInspection.warehouseConfirmation.status : null],
    receipt: [receivedTimestamp, receiptHistoryKnown, warehouseReceiptEvents.map(event => {
      const payload = event.payload && typeof event.payload === 'object' && !Array.isArray(event.payload) ? event.payload : null;
      return [receiptUuid(event.id), event.source === 'MERCHANT', nativeReceiptTimestamp(event.occurredAt),
        payload?.status === 'RECEIVED', nativeReceiptTimestamp(payload?.receivedAt)];
    })],
    lifecycle: [receiptAvailable, loading, nativeStatusLoading, markingAsReceived, retryReceiptConfirmation,
      receiptConfirmationNeedsReview, deliveryReceiptNeedsConfirmation, defectReviewBlocked, defectPlanShippingBlocked],
    native: nativeExchangeStatus,
  }, (_name, value) => value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.keys(value).sort().map(key => [key, value[key]])) : value);
  // Equivalent renders retain the action; each authority transition creates a new epoch.
  const receiptAuthorityOwner = useRef({scope: nativeScope, key: receiptAuthorityKey, allowed: receiptAvailable});
  if (receiptAuthorityOwner.current.scope !== nativeScope || receiptAuthorityOwner.current.key !== receiptAuthorityKey) {
    receiptAuthorityOwner.current = {scope: nativeScope, key: receiptAuthorityKey, allowed: receiptAvailable};
  }
  const receiptAuthority = receiptAuthorityOwner.current;
  const latestReceiptAuthority = useRef(receiptAuthority);
  latestReceiptAuthority.current = receiptAuthority;
  const pendingReceipt = useRef<object | null>(null);
  const receiptMounted = useRef(true);
  useEffect(() => {receiptMounted.current = true; return () => {receiptMounted.current = false;};}, []);

  const handleMarkAsReceived = async () => {
    if (!receiptMounted.current) {console.info('[ReturnsReceipt] GUARD_UNMOUNTED');return;}
    if (latestReceiptAuthority.current !== receiptAuthority) {console.info('[ReturnsReceipt] GUARD_STALE_AUTHORITY');return;}
    if (!receiptAuthority.allowed) {console.info('[ReturnsReceipt] GUARD_NOT_ALLOWED');return;}
    if (pendingReceipt.current) {console.info('[ReturnsReceipt] GUARD_PENDING');return;}
    const operation = {}; pendingReceipt.current = operation;
    try {
      console.info('[ReturnsReceipt] REQUEST_STARTED');
      await markAsReceived({
        variables: {
          input: {
            returnId,
            storeId,
          },
        },
      });
    } catch {
      console.warn('[ReturnsReceipt] CLIENT_FAILURE');
      showNotification(NotificationTypes.error, 'Could not confirm receipt. Refresh this request before trying again.');
    }
    finally {if (pendingReceipt.current === operation) pendingReceipt.current = null;}
  };

  const handleResolveRuleFlag = async (code?: string | null) => {
    if (resolutionProcessingUnavailable) return;
    if (!code || !returnId || !storeId || resolvingRuleFlagRef.current) return;

    resolvingRuleFlagRef.current = true;
    setResolvingRuleFlagCode(code);
    try {
      await resolveReturnFlag({
        variables: {
          input: {
            returnId,
            storeId,
            code,
          },
        },
      });

      try {
        await refetch();
        showNotification(NotificationTypes.success, 'Return flag resolved');
      } catch {
        showNotification(
          NotificationTypes.error,
          'Return flag resolved, but the return details could not be refreshed. Reload this page before continuing.',
        );
      }
    } catch (error) {
      const newError = apiErrorHandler(error as any);
      const timedOut = /504|gateway timeout|timed out|timeout/i.test(
        newError?.message || '',
      );
      showNotification(
        NotificationTypes.error,
        timedOut
          ? 'Resolving the review signal timed out. Refresh this return before trying again because the signal may already be resolved.'
          : newError?.message ||
              'An unexpected error occurred while resolving the return flag.',
      );
    } finally {
      resolvingRuleFlagRef.current = false;
      setResolvingRuleFlagCode(null);
    }
  };

  const handleRetryShopifyReturnSync = () => {
    if (!returnId || !storeId || resolutionProcessingUnavailable) return;

    retryShopifyReturnSync({
      variables: {
        input: {
          returnId,
          storeId,
        },
      },
    });
  };

  if (loading) return <LoadingScreen />;

  const returnData: Return = data?.returnRecord;

  if (!returnData) {
    return (
      <div className="p-8">
        <Back returnLocation={returnTo} />
        <Text value="Return not found" type="h4" className="mt-4" />
      </div>
    );
  }

  const currency = returnCurrency;
  const formatCurrency = (val: number) =>
    presentmentMoneyUnavailable ? 'Amount unavailable' : getStoreCurrencyFormatter(currency, val);
  const hasLabelCost =
    returnData.labelCost !== null && returnData.labelCost !== undefined;
  const hasCompleteLabel = hasCompleteReturnLabel(returnData);
  const hasRecordedSettlement = Boolean(
    returnData.settlementStatus === 'COMPLETED' ||
    returnData.refundedAt ||
    returnData.storeCreditIssuedAt,
  );
  const unsupportedRequestClosed = resolutionProcessingUnavailable &&
    [ReturnStatus.DENIED, ReturnStatus.EXPIRED, ReturnStatus.RESOLVED].includes(returnData.status);
  const showProcessingUnavailableStatus =
    resolutionProcessingUnavailable && !keepCreditManaged && !nativeExchangeVerified && !hasRecordedSettlement && !unsupportedRequestClosed;
  const statusLabel = keepCreditManaged
    ? !keepCreditStatus ? 'Credit status unverified' : keepCreditStatus.state === 'ISSUED' ? 'Store credit issued' : keepCreditStatus.state === 'CANCELLED' ? 'Credit request cancelled' : keepCreditStatus.state === 'MANUAL_REVIEW' ? 'Credit needs review' : keepCreditStatus.state === 'WAITING_APPROVAL' && keepCreditStatus.approvalRequired && !keepCreditStatus.approvalRecorded ? 'Awaiting credit approval' : 'Store credit pending'
    : nativeExchangeVerified && !unsupportedRequestClosed
    ? nativeApprovalNeeded ? 'Awaiting merchant approval' : nativeAlternative ? 'Store credit request' : 'Exchange request'
    : showProcessingUnavailableStatus
    ? 'Processing unavailable'
    : unsupportedRequestClosed && !hasRecordedSettlement
    ? formatWorkflowLabel(returnData.status)
    : returnData.businessStatusLabel ||
      formatWorkflowLabel(returnData.businessStatus || returnData.status);
  const selectedShippingOption = returnData.selectedShippingOption;
  const hasSelectedShippingOption = Boolean(
    selectedShippingOption?.source ||
      selectedShippingOption?.courierServiceId ||
      selectedShippingOption?.displayName,
  );
  const selectedShippingOptionLabel =
    selectedShippingOption?.displayName ||
    [selectedShippingOption?.carrier, selectedShippingOption?.service]
      .filter(Boolean)
      .join(' / ') ||
    selectedShippingOption?.courierServiceId ||
    'Configured return shipping option';
  const selectedShippingOptionSourceLabel = selectedShippingOption?.source
    ? SHIPPING_OPTION_SOURCE_LABELS[selectedShippingOption.source] ||
      selectedShippingOption.source.replace(/_/g, ' ')
    : null;
  const isLabelPending =
    returnData.labelSource === 'EASYSHIP_SHIPAID_RATES_PENDING';
  const hasShippingInfo =
    hasSelectedShippingOption ||
    returnData.shippingId ||
    returnData.labelUrl ||
    returnData.trackingNumber ||
    returnData.carrier ||
    returnData.service ||
    hasLabelCost ||
    returnData.labelSource;
  const labelSource = returnData.labelSource
    ? LABEL_SOURCE_LABELS[returnData.labelSource] ||
      returnData.labelSource.replace(/_/g, ' ')
    : null;
  const isCheckingEasyshipConnection =
    isMerchantCarrierLabelSource && easyshipConnectionLoading;
  const couldNotVerifyEasyshipConnection =
    isMerchantCarrierLabelSource && Boolean(easyshipConnectionQueryError);
  const requiresEasyshipConnection =
    isMerchantCarrierLabelSource &&
    !isCheckingEasyshipConnection &&
    !couldNotVerifyEasyshipConnection &&
    !isEasyshipConnected;
  const labelErrorBlocksCreation = activeLabelErrorPresentation
    ? activeLabelErrorPresentation.retryDisposition === 'RETRY_LATER' &&
      activeLabelErrorPresentation.outcome === 'NOT_CREATED' &&
      !activeLabelErrorPresentation.mayHaveCreatedLabel
      ? !labelRetryReady
      : !activeLabelErrorPresentation.canRetry
    : false;
  const baseCanCreateLabel =
    !resolutionProcessingUnavailable &&
    !defectReviewBlocked &&
    !defectPlanShippingBlocked &&
    returnData.status === ReturnStatus.APPROVED &&
    !hasCompleteLabel &&
    returnData.deflectionOption !== 'KEEP_ITEM_DISCOUNT';
  const canCreateLabel =
    baseCanCreateLabel &&
    !(isDefectReturn && (
      refunding ||
      creatingStoreCredit ||
      ['PROCESSING', 'OUTCOME_UNKNOWN', 'COMPLETED'].includes(
        returnData.settlementStatus || '',
      )
    )) &&
    !isCheckingEasyshipConnection &&
    !couldNotVerifyEasyshipConnection &&
    !requiresEasyshipConnection &&
    !labelErrorBlocksCreation;
  const labelCreationMessage =
    labelRetryReady &&
    activeLabelErrorPresentation?.retryDisposition === 'RETRY_LATER' &&
    activeLabelErrorPresentation.outcome === 'NOT_CREATED'
      ? 'You can try creating the label again now.'
      : activeLabelErrorPresentation?.message
      ? activeLabelErrorPresentation.message
      : isCheckingEasyshipConnection
      ? 'Checking Easyship connection...'
      : couldNotVerifyEasyshipConnection
      ? 'Could not verify the Easyship connection. Retry the connection check before creating this label.'
      : requiresEasyshipConnection
      ? 'Easyship authorization is required before this carrier-account label can be created.'
      : '';
  const configuredPolicyMode =
    storeProperties?.policySettings?.strictness?.mode ||
    PolicyStrictnessMode.CUSTOMER_FIRST;
  const configuredPolicyBehavior = getPolicyModeBehavior(configuredPolicyMode);
  const configuredEligibleApprovalMode =
    storeProperties?.policySettings?.strictness?.eligibleApprovalMode ||
    configuredPolicyBehavior.eligibleApprovalMode;
  const policyApprovalMode = returnData.policy?.approvalMode;

  const paidItemSubtotal = returnData.returnItems.reduce((acc, item) => {
    if (!item.orderItem) return acc;
    const paidTotal = item.orderItem.total;
    const orderedQuantity = item.orderItem.quantity;
    const unitAmount =
      typeof paidTotal === 'number' &&
      Number.isFinite(paidTotal) &&
      paidTotal >= 0 &&
      typeof orderedQuantity === 'number' &&
      orderedQuantity > 0
        ? paidTotal / orderedQuantity
        : item.orderItem.price;
    return acc + unitAmount * item.quantity;
  }, 0);
  const itemSubtotal = roundReturnMoney(paidItemSubtotal, currency);

  const snapshotFees = returnData.policySnapshot?.dynamicFees;
  const dynamicFees = Array.isArray(snapshotFees)
    ? snapshotFees
    : returnData.policy?.dynamicFees ?? [];
  const feeAmount = (fee: ReturnDynamicFee) =>
    parseReturnFeeAmount(fee.calculatedAmount) ?? (fee.type === 'PERCENTAGE'
      ? (itemSubtotal * (fee.value ?? 0)) / 100
      : (fee.value ?? 0));
  const unroundedFeesAmount = dynamicFees.reduce(
    (total, fee) => total + feeAmount(fee),
    0,
  );
  // Legacy settlement rounds the aggregate, not each percentage fee.
  const totalFeesAmount = roundReturnMoney(unroundedFeesAmount, currency);

  const baseNetAmount = roundReturnMoney(
    Math.max(0, itemSubtotal - totalFeesAmount), currency,
  );

  let totalRefundAmount = baseNetAmount;
  let deflectionAdjustment = 0;
  const returnMeta =
    returnData.meta &&
    typeof returnData.meta === 'object' &&
    !Array.isArray(returnData.meta)
      ? returnData.meta
      : {};
  const selectedDeflectionOffer =
    returnMeta.selectedDeflectionOffer &&
    typeof returnMeta.selectedDeflectionOffer === 'object' &&
    !Array.isArray(returnMeta.selectedDeflectionOffer)
      ? (returnMeta.selectedDeflectionOffer as {
          type?: string;
          label?: string;
          value?: number | string;
          valueType?: string;
          offerType?: string;
        })
      : null;
  const snapshotOffers = returnData.policySnapshot?.deflectionOffers;
  const policySelectedOffer = (Array.isArray(snapshotOffers)
    ? snapshotOffers
    : returnData.policy?.deflectionOffers)?.find(
    (o) => o && typeof o === 'object' && o.type && o.type === returnData.deflectionOption,
  );
  const selectedOffer = selectedDeflectionOffer || policySelectedOffer;
  const selectedKeepItemOfferType = String(
    selectedOffer?.offerType || '',
  ).toUpperCase();
  const selectedKeepItemOfferLabel = String(
    selectedOffer?.label || '',
  ).toLowerCase();
  const keepItemUsesDiscountCode =
    selectedKeepItemOfferType === 'DISCOUNT_CODE' ||
    selectedKeepItemOfferLabel.includes('discount code');
  const keepItemUsesStoreCredit =
    selectedKeepItemOfferType === 'STORE_CREDIT' ||
    selectedKeepItemOfferLabel.includes('store credit');
  const keepItemApprovalActionLabel = keepItemUsesDiscountCode
    ? 'Approve & create discount code'
    : keepItemUsesStoreCredit
    ? 'Approve & issue store credit'
    : 'Approve & issue incentive';
  const keepItemIssueActionLabel = keepItemUsesDiscountCode
    ? 'Create discount code'
    : keepItemUsesStoreCredit
    ? 'Issue store credit'
    : 'Issue incentive';
  const keepItemSettlementLabel = keepItemUsesStoreCredit
    ? 'Store credit reward'
    : keepItemUsesDiscountCode
    ? 'Discount code reward'
    : 'Keep-item incentive';
  const isSettlementNextAction = [
    'VERIFY_SETTLEMENT',
    'SETTLEMENT_IN_PROGRESS',
    'REVIEW_SETTLEMENT',
  ].includes(returnData.nextAction || '');
  const displayedNextAction = isSettlementNextAction
    ? returnData.nextAction
    : returnData.status === ReturnStatus.APPROVED &&
      returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
    ? keepItemUsesDiscountCode
      ? 'ISSUE_DISCOUNT_CODE'
      : 'ISSUE_STORE_CREDIT'
    : returnData.nextAction;
  const deflectionReward =
    returnMeta.deflectionReward &&
    typeof returnMeta.deflectionReward === 'object' &&
    !Array.isArray(returnMeta.deflectionReward)
      ? (returnMeta.deflectionReward as {
          option?: string;
          amount?: number | string;
          code?: string;
          issuedAt?: string;
        })
      : null;
  const deflectionRewardOption = String(
    deflectionReward?.option || '',
  ).toUpperCase();
  const deflectionRewardAmount = Number(deflectionReward?.amount ?? 0);
  const deflectionRewardCode =
    typeof deflectionReward?.code === 'string' ? deflectionReward.code : '';
  const hasDeflectionReward =
    Boolean(deflectionReward) &&
    ((Number.isFinite(deflectionRewardAmount) && deflectionRewardAmount > 0) ||
      Boolean(deflectionRewardCode));
  const returnsFeatures = (storeProperties?.features as any)?.returns || {};
  const shopifyReturnSyncSettings =
    returnsFeatures.shopifyNativeReturnSync ||
    returnsFeatures.shopify_native_return_sync ||
    {};
  const shopifyNativeReturnSyncEnabled = Boolean(
    shopifyReturnSyncSettings.enabled ??
      shopifyReturnSyncSettings.isEnabled ??
      returnsFeatures.shopifyNativeReturnSyncEnabled ??
      returnsFeatures.shopify_native_return_sync_enabled,
  );
  const hasShopifyReturnSyncData = Boolean(
    returnData.shopifyReturnId ||
      returnData.shopifyReturnName ||
      returnData.shopifyReturnStatus ||
      returnData.shopifyReturnSyncStatus ||
      returnData.shopifyReturnSyncError ||
      returnData.shopifyReturnSyncedAt ||
      returnData.shopifyReturnLastWebhookAt ||
      returnData.shopifyReverseFulfillmentOrderIds?.length,
  );
  const nativeExchangeOwnsReturnSync = Boolean(
    !nativeStatusLoading && nativeExchangeStatus &&
    ['WAITING_TRIGGER', 'CREATED', 'PROCESSING', 'PROCESSED', 'READY'].includes(nativeExchangeStatus.status) &&
    returnData.id === returnId && returnData.storeId === storeId &&
    nativeExchangeStatus.returnId === returnData.id &&
    returnData.requestedResolution === 'EXCHANGE' &&
    returnData.returnItems.length === 1 && returnData.returnItems[0].requestedResolution === 'EXCHANGE' &&
    Object.prototype.hasOwnProperty.call(returnData.meta ?? {}, 'nativeExchangeOperationId') &&
    receiptUuid(returnData.meta?.nativeExchangeOperationId),
  );
  const shouldShowShopifyReturnSyncCard =
    hasShopifyReturnSyncData || (shopifyNativeReturnSyncEnabled && !nativeExchangeOwnsReturnSync);
  const shopifyReturnSyncPresentation = getShopifyReturnSyncPresentation({
    returnStatus: returnData.status,
    syncStatus: returnData.shopifyReturnSyncStatus,
    shopifyReturnId: returnData.shopifyReturnId,
  });
  const shopifyReturnSyncStatus = shopifyReturnSyncPresentation.status;
  const shopifyReturnSyncStatusClass =
    SHOPIFY_RETURN_SYNC_STATUS_CLASSES[shopifyReturnSyncStatus] ||
    'border-gray-200 bg-gray-50 text-gray-700';
  const canRetryShopifyReturnSync = !resolutionProcessingUnavailable && shopifyReturnSyncPresentation.canRetry;

  if (selectedOffer) {
    if (selectedOffer.type === 'KEEP_ITEM_DISCOUNT') {
      if (selectedOffer.valueType === 'PERCENTAGE') {
        totalRefundAmount =
          (baseNetAmount * Number(selectedOffer.value || 0)) / 100;
      } else {
        totalRefundAmount = Number(selectedOffer.value || 0);
      }
      totalRefundAmount = roundReturnMoney(totalRefundAmount, currency);
      deflectionAdjustment = roundReturnMoney(totalRefundAmount - baseNetAmount, currency);
    } else if (selectedOffer.type === 'STORE_CREDIT_BONUS') {
      if (selectedOffer.valueType === 'PERCENTAGE') {
        deflectionAdjustment =
          (itemSubtotal * Number(selectedOffer.value || 0)) / 100;
      } else {
        deflectionAdjustment = Number(selectedOffer.value || 0);
      }
      deflectionAdjustment = roundReturnMoney(deflectionAdjustment, currency);
      totalRefundAmount = roundReturnMoney(
        baseNetAmount + deflectionAdjustment,
        currency,
      );
    }
  }

  const approvedInspectionAmount = returnRequiresInspection(returnData) && !inspectionSettlementBlocked
    ? returnData.meta?.inspection?.approvedAmount as number
    : undefined;
  const hasStoreCreditBonus = returnData.deflectionOption === 'STORE_CREDIT_BONUS' &&
    Number.isFinite(deflectionAdjustment) && deflectionAdjustment > 0;
  const plainStoreCredit = storeCreditRequested &&
    returnData.deflectionOption !== 'KEEP_ITEM_DISCOUNT' && !hasStoreCreditBonus;

  const getReasonSnippet = () => {
    const uniqueReasons = Array.from(
      new Set(returnData.returnItems.map((i) => i.reason).filter(Boolean)),
    );
    if (uniqueReasons.length === 0) return 'No reason provided';
    return uniqueReasons.map((reason) => formatReturnReason(reason)).join(', ');
  };

  const totalReturnQuantity = returnData.returnItems.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0,
  );
  const returnItemSummary = `${totalReturnQuantity} ${
    totalReturnQuantity === 1 ? 'unit' : 'units'
  } across ${returnData.returnItems.length} ${
    returnData.returnItems.length === 1 ? 'item' : 'items'
  }`;
  const requestedOutcome = keepCreditManaged ? 'Keep item · Store credit' : nativeExchangeVerified
    ? nativeAlternative ? 'Store credit instead of exchange' : 'Exchange'
    : resolutionProcessingUnavailable
    ? `${unsupportedResolutions.map(formatWorkflowLabel).join(', ')} (unsupported resolution)`
    : isDefectReturn
    ? defectRequestClosed
      ? 'Request closed'
      : defectReviewApproved
      ? 'Merchant will select the resolution'
      : 'Resolution pending evidence review'
    : plainStoreCredit
    ? 'Store credit'
    : selectedOffer?.label
    ? selectedOffer.label
    : returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
    ? 'Keep item incentive'
    : returnData.deflectionOption === 'STORE_CREDIT_BONUS'
    ? 'Store credit incentive'
    : storeCreditRequested
    ? 'Store credit'
    : 'Standard return';

  const approvalInsight =
    policyApprovalMode === ReturnApprovalMode.AUTO_APPROVE
      ? {
          tone: 'green' as const,
          title: 'Eligible for instant approval',
          description:
            'Customer-First mode is active, and this request passed the configured rules without a review hold.',
        }
      : returnData.status === ReturnStatus.SUBMITTED
      ? configuredPolicyMode === PolicyStrictnessMode.SEMI_AUTOMATIC ||
        configuredEligibleApprovalMode === ReturnApprovalMode.MANUAL_REVIEW
        ? {
            tone: 'amber' as const,
            title: 'Awaiting team approval',
            description:
              'Semi-Automatic mode screens requests first, but every eligible return still waits for a human decision.',
          }
        : configuredPolicyMode === PolicyStrictnessMode.CUSTOMER_FIRST
        ? {
            tone: 'amber' as const,
            title: 'Held for manual review',
            description:
              'Customer-First mode is active, so this request likely matched an exclusion or guardrail and needs a team decision.',
          }
        : {
            tone: 'amber' as const,
            title: 'Awaiting manual review',
            description:
              'Strict mode sends every return request to your team before it can be approved.',
          }
      : null;

  const approvalInsightClasses =
    approvalInsight?.tone === 'green'
      ? 'bg-green-50 border-green-100 text-green-700'
      : 'bg-amber-50 border-amber-100 text-amber-800';
  const workflowReviewReasons = returnData.reviewReasons || [];
  const workflowRuleFlags: ReturnDashboardFlag[] = (
    returnData.ruleFlags || []
  ).filter(Boolean);
  const hasWorkflowRiskLevel = Boolean(
    returnData.riskLevel && returnData.riskLevel.toUpperCase() !== 'NONE',
  );
  const hasActionableReviewSignals = Boolean(
    returnData.reviewRequired ||
      workflowReviewReasons.length ||
      workflowRuleFlags.length ||
      returnData.settlementFailureReason,
  );
  const refundActionStateBase = getSettlementActionState({
    kind: 'REFUND',
    readyLabel: defectReviewApproved ? 'Issue refund' : undefined,
    returnId,
    storeId,
    reviewRequired: returnData.reviewRequired,
    ruleFlags: workflowRuleFlags,
    settlementFailureReason: returnData.settlementFailureReason,
    settlementStatus: returnData.settlementStatus,
    settling:
      refunding || (isDefectReturn && (creatingStoreCredit || creatingLabel)),
  });
  const storeCreditActionStateBase = getSettlementActionState({
    kind: 'STORE_CREDIT',
    readyLabel: defectReviewApproved || plainStoreCredit ? 'Issue store credit' : undefined,
    returnId,
    storeId,
    reviewRequired: returnData.reviewRequired,
    ruleFlags: workflowRuleFlags,
    settlementFailureReason: returnData.settlementFailureReason,
    settlementStatus: returnData.settlementStatus,
    settling:
      creatingStoreCredit || (isDefectReturn && (refunding || creatingLabel)),
  });
  const inspectionInstruction = 'Complete the required receipt inspection before issuing funds.';
  const precisionInstruction = presentmentMoneyUnavailable
    ? 'Authoritative return currency amounts are unavailable. Contact ShipAid support for review.'
    : 'Saved return amount precision requires review. Refresh and revise this request before issuing funds.';
  const settlementInstruction = financialReviewRequired ? precisionInstruction : inspectionInstruction;
  const refundActionState = inspectionSettlementBlocked || financialReviewRequired
    ? { ...refundActionStateBase, disabled: true, instruction: settlementInstruction }
    : refundActionStateBase;
  const storeCreditActionState = inspectionSettlementBlocked || financialReviewRequired
    ? { ...storeCreditActionStateBase, disabled: true, instruction: settlementInstruction }
    : storeCreditActionStateBase;
  const deflectionActionStateBase = getSettlementActionState({
    kind: 'DEFLECTION',
    returnId,
    storeId,
    reviewRequired: returnData.reviewRequired,
    ruleFlags: workflowRuleFlags,
    settlementFailureReason: returnData.settlementFailureReason,
    settlementStatus: returnData.settlementStatus,
    settling: resolvingDeflection,
    readyLabel: keepItemIssueActionLabel,
    retryLabel: `Retry ${keepItemSettlementLabel}`,
    processingLabel: `${keepItemSettlementLabel} in progress`,
    verificationLabel: `${keepItemSettlementLabel} needs verification`,
    settlementName: keepItemSettlementLabel.toLowerCase(),
  });
  const deflectionActionState = financialReviewRequired
    ? { ...deflectionActionStateBase, disabled: true, instruction: precisionInstruction }
    : deflectionActionStateBase;
  const needsShopifyReauthorization = Boolean(
    storeProperties?.shouldUpdateAppInstall ||
      showShopifyReconnectCta ||
      isShopifyAuthorizationErrorMessage(returnData.settlementFailureReason),
  );
  const workflowEvents = [...(returnData.events || [])].sort(
    (a, b) => getDateTimeMs(b.occurredAt) - getDateTimeMs(a.occurredAt),
  );
  const workflowDetails = [
    {
      label: 'Ship by',
      value: returnData.shipByAt
        ? formatWorkflowDate(returnData.shipByAt)
        : null,
    },
    {
      label: 'Refund due',
      value: returnData.refundDueAt
        ? formatWorkflowDate(returnData.refundDueAt)
        : null,
    },
    {
      label: 'Approved',
      value: returnData.approvedAt
        ? formatWorkflowDate(returnData.approvedAt)
        : null,
    },
    {
      label: 'Label created',
      value: returnData.labelCreatedAt
        ? formatWorkflowDate(returnData.labelCreatedAt)
        : null,
    },
    {
      label: 'Tracking',
      value: returnData.trackingStatus
        ? formatWorkflowLabel(returnData.trackingStatus)
        : null,
      subValue: returnData.trackingUpdatedAt
        ? `Updated ${formatWorkflowDate(returnData.trackingUpdatedAt)}`
        : undefined,
    },
    {
      label: 'First carrier scan',
      value: returnData.firstCarrierScanAt
        ? formatWorkflowDate(returnData.firstCarrierScanAt)
        : null,
    },
    {
      label: 'Received',
      value: returnData.receivedAt
        ? formatWorkflowDate(returnData.receivedAt)
        : returnData.inboundDeliveredAt
        ? formatWorkflowDate(returnData.inboundDeliveredAt)
        : null,
    },
    {
      label: 'Refunded',
      value: returnData.refundedAt
        ? formatWorkflowDate(returnData.refundedAt)
        : null,
    },
    {
      label: 'Store credit',
      value: returnData.storeCreditIssuedAt
        ? formatWorkflowDate(returnData.storeCreditIssuedAt)
        : null,
    },
    {
      label: 'Expired',
      value: returnData.expiredAt
        ? formatWorkflowDate(returnData.expiredAt)
        : null,
    },
  ].filter((item) => item.value);

  const shopifyOrderUrl = getShopifyOrderAdminUrl(
    storeDomain,
    returnData.order.orderId,
  );

  return (
    <div className="max-w-7xl mx-auto bg-white p-6 shadow sm:rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center">
            <Back returnLocation={returnTo} />
            <div className="ml-5">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-xl font-bold text-gray-900">
                  {keepCreditManaged ? 'Keep item · Store credit' : getRequestTypeLabel({type: 'RETURN', requestedResolution: returnData.requestedResolution,
                    items: returnData.returnItems})} {returnData.number}
                </h1>
                {shopifyOrderUrl && (
                  <Link
                    to={shopifyOrderUrl}
                    aria-label="View order in Shopify"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLinkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-3">
                <StatusLabel
                  label={statusLabel}
                  type={showProcessingUnavailableStatus
                    ? 'warning'
                    : businessStatusToType(returnData.businessStatus || returnData.status)}
                />
                <span className="text-sm text-gray-500">
                  Created on{' '}
                  {format(new Date(returnData.createdAt), 'do MMM yyyy hh:mm')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            {!keepCreditManaged && !isDefectReturn && returnData.status === ReturnStatus.SUBMITTED && (
              <>
                <Button
                  onClick={() => setDenyModalOpen(true)}
                  color="danger"
                  variant="outlined"
                  disabled={updatingStatus}
                >
                  Deny
                </Button>
                {!resolutionProcessingUnavailable && <Button
                  onClick={handleApprove}
                  className="bg-green-600 border border-green-600 hover:bg-green-700 text-white"
                  isLoading={updatingStatus}
                  disabled={warehouseApprovalBlocked}
                >
                  {returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
                    ? keepItemApprovalActionLabel
                    : 'Approve'}
                </Button>}
              </>
            )}

            {!resolutionProcessingUnavailable && !defectReviewBlocked && !defectPlanShippingBlocked && returnData.status === ReturnStatus.APPROVED &&
              !hasCompleteLabel && (
                <>
                  {returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT' ? (
                    keepItemUsesStoreCredit || keepItemUsesDiscountCode ? (
                      <ReturnStoreCreditActionControls
                        actionId="return-deflection-action"
                        actionState={deflectionActionState}
                        actionClassName="bg-indigo-600 border border-indigo-600 hover:bg-indigo-700 text-white"
                        actionLoading={resolvingDeflection}
                        needsShopifyReauthorization={
                          needsShopifyReauthorization
                        }
                        onAction={handleResolveDeflection}
                        onReconnect={handleReconnectShopify}
                        reconnectLabel={
                          keepItemUsesDiscountCode
                            ? 'Reconnect Shopify to issue the discount code reward'
                            : 'Reconnect Shopify to issue the store credit reward'
                        }
                        reconnecting={reauthorizingShopify}
                      />
                    ) : (
                      <ReturnSettlementActionControl
                        actionId="return-deflection-action"
                        actionState={deflectionActionState}
                        onClick={handleResolveDeflection}
                        className="bg-indigo-600 border border-indigo-600 hover:bg-indigo-700 text-white"
                        isLoading={resolvingDeflection}
                      />
                    )
                  ) : (
                    <Button
                      onClick={() =>
                        canCreateLabel &&
                        createLabel({
                          variables: {
                            input: { returnId: returnId!, storeId },
                          },
                        })
                      }
                      className="bg-blue-600 border border-blue-600 hover:bg-blue-700 text-white"
                      isLoading={creatingLabel}
                      disabled={!canCreateLabel || isLabelPending}
                    >
                      {isLabelPending
                        ? 'Label reconciliation required'
                        : 'Create label'}
                    </Button>
                  )}
                </>
              )}

            {receiptAvailable && (
              <Button
                onClick={handleMarkAsReceived}
                className="bg-indigo-600 border border-indigo-600 hover:bg-indigo-700 text-white"
                isLoading={markingAsReceived}
              >
                {retryReceiptConfirmation ? 'Retry receipt confirmation' : queriedReturnData?.meta?.inspection?.warehouseConfirmation?.status === 'REQUESTED' ? 'Confirm warehouse receipt' : 'Mark as Received'}
              </Button>
            )}

            {!keepCreditManaged && !defectReviewBlocked && (returnData.status === ReturnStatus.RECEIVED || (defectReviewApproved && returnData.status === ReturnStatus.APPROVED)) && (
              <>
                {!defectResolutionManaged && !returnRequiresInspection(returnData) && <Button
                  onClick={() => setDenyModalOpen(true)}
                  color="danger"
                  variant="outlined"
                  disabled={updatingStatus || refunding || creatingStoreCredit}
                >
                  Deny After Inspection
                </Button>}
                {!resolutionProcessingUnavailable && !defectResolutionManaged && (defectReviewApproved || storeCreditRequested) && (
                  <ReturnStoreCreditActionControls
                    actionId="return-store-credit-action"
                    actionState={storeCreditActionState}
                    actionClassName="bg-indigo-600 border border-indigo-600 hover:bg-indigo-700 text-white"
                    actionLoading={creatingStoreCredit}
                    needsShopifyReauthorization={needsShopifyReauthorization}
                    onAction={handleStoreCredit}
                    onReconnect={handleReconnectShopify}
                    reconnectLabel="Reconnect Shopify to issue store credit"
                    reconnecting={reauthorizingShopify}
                  />
                )}
                {!resolutionProcessingUnavailable && !defectResolutionManaged && (defectReviewApproved || !storeCreditRequested) && (
                  <ReturnStoreCreditActionControls
                    actionId="return-refund-action"
                    actionState={refundActionState}
                    actionClassName="bg-green-600 border border-green-600 hover:bg-green-700 text-white"
                    actionLoading={refunding}
                    needsShopifyReauthorization={needsShopifyReauthorization}
                    onAction={handleRefund}
                    onReconnect={handleReconnectShopify}
                    reconnectLabel="Reconnect Shopify to process the refund"
                    reconnecting={reauthorizingShopify}
                  />
                )}
              </>
            )}
          </div>

          {baseCanCreateLabel && labelCreationMessage && (
            <div className="max-w-sm text-right text-xs text-amber-700">
              <p>{labelCreationMessage}</p>
              {activeLabelErrorPresentation?.fieldErrors.length ? (
                <ul className="mt-1 list-inside list-disc">
                  {activeLabelErrorPresentation.fieldErrors.map(
                    (fieldError) => (
                      <li
                        key={`${fieldError.owner}-${fieldError.field}-${fieldError.code}`}
                      >
                        {fieldError.message}
                      </li>
                    ),
                  )}
                </ul>
              ) : null}
              {activeLabelErrorPresentation?.supportReference && (
                <p className="mt-1">
                  Support reference:{' '}
                  <span className="font-semibold">
                    {activeLabelErrorPresentation.supportReference}
                  </span>
                </p>
              )}
              {labelPollingExhausted &&
                activeLabelErrorPresentation?.shouldPoll && (
                  <>
                    <p className="mt-1 font-medium">
                      Automatic refresh checks are complete. Refresh this return
                      or contact SHIPAID support.
                    </p>
                    <button
                      type="button"
                      className="mt-1 mr-2 font-semibold text-blue-600 hover:text-blue-700"
                      onClick={() => {
                        refetch().catch(() =>
                          showNotification(
                            NotificationTypes.error,
                            'Could not refresh this return. Try again shortly.',
                          ),
                        );
                      }}
                    >
                      Refresh return
                    </button>
                    <a
                      href="mailto:support@shipaid.com"
                      className="mt-1 inline-block font-semibold text-blue-600 hover:text-blue-700"
                    >
                      Contact SHIPAID support
                    </a>
                  </>
                )}
              {couldNotVerifyEasyshipConnection && (
                <button
                  type="button"
                  className="mt-1 font-semibold text-blue-600 hover:text-blue-700"
                  onClick={() => {
                    refetchEasyshipConnection?.().catch(() =>
                      showNotification(
                        NotificationTypes.error,
                        'Could not verify the Easyship connection. Try again shortly.',
                      ),
                    );
                  }}
                >
                  Retry connection check
                </button>
              )}
              {requiresEasyshipConnection && (
                <a
                  href="mailto:support@shipaid.com"
                  className="mt-1 inline-block font-semibold text-blue-600 hover:text-blue-700"
                >
                  Contact SHIPAID support
                </a>
              )}
              {activeLabelErrorPresentation?.action === 'REFRESH_RETURN' && (
                <button
                  type="button"
                  className="mt-1 font-semibold text-blue-600 hover:text-blue-700"
                  onClick={refreshLabelReadiness}
                >
                  Refresh return
                </button>
              )}
              {activeLabelErrorPresentation?.actionTarget &&
                activeLabelErrorPresentation.actionLabel &&
                (activeLabelErrorPresentation.actionTarget.startsWith('/') ? (
                  <Link
                    to={activeLabelErrorPresentation.actionTarget}
                    className="mt-1 inline-block font-semibold text-blue-600 hover:text-blue-700"
                  >
                    {activeLabelErrorPresentation.actionLabel}
                  </Link>
                ) : (
                  <a
                    href={activeLabelErrorPresentation.actionTarget}
                    className="mt-1 inline-block font-semibold text-blue-600 hover:text-blue-700"
                    target={
                      activeLabelErrorPresentation.actionTarget.startsWith(
                        'http',
                      )
                        ? '_blank'
                        : undefined
                    }
                    rel={
                      activeLabelErrorPresentation.actionTarget.startsWith(
                        'http',
                      )
                        ? 'noreferrer'
                        : undefined
                    }
                  >
                    {activeLabelErrorPresentation.actionLabel}
                  </a>
                ))}
            </div>
          )}
        </div>
      </div>

      {financialReviewRequired && (
        <p role="alert" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {precisionInstruction}
        </p>
      )}
      {resolutionProcessingUnavailable && !keepCreditManaged && !nativeExchangeVerified && (
        <div role="alert" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Processing is unavailable for this return resolution.</p>
          <p className="mt-1">{unsupportedRequestClosed
            ? 'The recorded history remains available for this closed request.'
            : 'Review the recorded history and coordinate manual handling before taking further action.'}</p>
        </div>
      )}

      {storeId && returnId && nativeReviewCandidate && <NativeExchangeReview
        key={`${returnData.id}:${returnData.updatedAt}:${instructionRefreshRevision}`}
        storeId={storeId}
        returnId={returnId}
        returnReceived={returnData.status === ReturnStatus.RECEIVED}
        closed={[ReturnStatus.DENIED, ReturnStatus.EXPIRED, ReturnStatus.RESOLVED].includes(returnData.status)}
        onStatusChange={onNativeStatusChange}
        onLoadingChange={onNativeLoadingChange}
        onApproved={() => {if (nativeOwner.current === nativeScope) void Promise.resolve(refetch()).catch(() => {});}}
      />}
      {storeId && returnId && nativeReviewCandidate && <ReturnCustomizationReview
        key={`customization:${returnData.id}:${returnData.updatedAt}`}
        storeId={storeId} returnId={returnId} orderNumber={returnData.order.number}
        replacementTitle={nativeExchangeStatus?.replacement?.title}
        onAcknowledged={() => {if (nativeOwner.current === nativeScope) {setInstructionRefreshRevision(value => value + 1);void Promise.resolve(refetch()).catch(() => {});}}}
      />}
      {storeId && returnId && keepCreditManaged && <KeepItemCreditReview
        key={`${returnData.id}:${returnData.updatedAt}`}
        storeId={storeId} returnId={returnId} record={returnData}
        onStatusChange={onKeepCreditStatusChange}
        onApproved={() => {if (nativeOwner.current === nativeScope) void Promise.resolve(refetch()).catch(() => {});}}
      />}

      {receiptConfirmationNeedsReview && <div role="alert" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">Receipt confirmation needs review.</p>
        <p>The saved receipt time or warehouse history could not be verified. Contact support before recording another receipt.</p>
      </div>}

      <OrderRequestsPanel
        orderId={returnData.orderId}
        currentRequest={{ type: 'RETURN', id: returnData.id }}
        refreshKey={returnData.updatedAt}
      />
      {!defectPlanShippingBlocked && (storeProperties?.policySettings?.operationalControls?.logistics?.returnLocations || []).length > 0 && (
        <ReturnWarehouseRoutingPanel
          record={returnData}
          locations={storeProperties!.policySettings!.operationalControls!.logistics!.returnLocations!}
          onEditingChange={setWarehouseEditing}
          onSaved={async () => {
            setApprovalModalOpen(false);
            const refreshed = await refetch();
            return refreshed.data?.returnRecord;
          }}
        />
      )}
      {!defectPlanShippingBlocked && returnRequiresInspection(returnData) && (
        <ReturnInspectionPanel record={returnData} currency={returnCurrency} onSaved={async () => { await refetch(); }} />
      )}
      {isDefectReturn && !keepCreditManaged && !nativeExchangeVerified && (
        <DefectReviewPanel
          status={defectReviewDetailsReady ? defectReview.status : 'PENDING'}
          closed={defectRequestClosed}
          images={returnData.images}
          photosRequired={defectPhotosRequired}
          videos={returnData.videos}
          acknowledged={defectEvidenceAcknowledged}
          detailsReady={defectReviewDetailsReady}
          canDecide={
            returnData.status === ReturnStatus.SUBMITTED &&
            defectReview?.status === 'PENDING'
          }
          approvalUnavailable={resolutionProcessingUnavailable || warehouseApprovalBlocked}
          updating={updatingStatus}
          onAcknowledge={(checked) =>
            setReviewedDefectVersion(checked ? defectEvidenceVersion : null)
          }
          onApprove={handleApprove}
          onDeny={() => setDenyModalOpen(true)}
          onReload={() => refetch()}
        />
      )}
      {isDefectReturn && !keepCreditManaged && !nativeExchangeVerified && storeId && <DefectResolutionWorkflow key={returnData.id} record={returnData} storeId={storeId}
        onUpdated={() => refetch()} onWorkflowChange={setDefectWorkflow} />}

      <section className="mb-8 overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            Decision summary
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Review the request, status, and next step.
          </p>
        </div>

        <div className="space-y-5 p-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                Customer request
              </p>
              <p className="mt-2 text-lg font-semibold text-gray-900">
                {getReasonSnippet()}
              </p>
              <p className="mt-1 text-sm text-gray-600">{returnItemSummary}</p>
              <p className="mt-3 text-xs text-gray-500">
                Requested outcome:{' '}
                <span className="font-semibold text-gray-700">
                  {requestedOutcome}
                </span>
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                Current status
              </p>
              <p className="mt-2 text-lg font-semibold text-gray-900">
                {statusLabel}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Filed {formatWorkflowDate(returnData.createdAt)}
              </p>
              {hasWorkflowRiskLevel && (
                <span
                  className={`mt-3 inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getReviewSignalClasses(
                    returnData.riskLevel,
                  )}`}
                >
                  {formatWorkflowLabel(returnData.riskLevel)} risk
                </span>
              )}
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-600">
                Next action
              </p>
              <p className="mt-2 text-lg font-semibold text-gray-900">
                {keepCreditManaged ? keepCreditStatus ? keepCreditStatus.state === 'WAITING_APPROVAL' && keepCreditStatus.approvalRequired && !keepCreditStatus.approvalRecorded ? 'Review credit request' : 'Monitor credit request' : 'Verify credit status'
                  : nativeExchangeVerified && !unsupportedRequestClosed
                  ? nativeAlternative ? nativeApprovalNeeded ? 'Review store credit request' : 'Monitor store credit request' : nativeApprovalNeeded ? 'Review exchange' : 'Monitor exchange'
                  : unsupportedRequestClosed
                  ? 'No further action'
                  : resolutionProcessingUnavailable
                  ? 'Manual handling required'
                  : defectRequestClosed
                  ? 'No further action'
                  : defectReviewBlocked && displayedNextAction !== 'AWAITING_CUSTOMER_INFORMATION'
                  ? 'Review evidence'
                  : getNextActionLabel(displayedNextAction)}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-gray-600">
                {keepCreditManaged ? keepCreditStatus ? 'Review the accepted store credit details above. No return shipment or replacement is required.' : 'Reload credit status to verify this request before taking action.'
                  : nativeExchangeVerified && !unsupportedRequestClosed
                  ? nativeAlternative ? 'Review the store credit details above. The original item must be returned; no replacement will be sent.'
                    : nativeShippingBlocked ? 'Review the exchange details above. The customer keeps the original item; approval and verified payment checks still apply.'
                    : 'Review the exchange details above. The configured return and verified payment checks still apply.'
                  : unsupportedRequestClosed
                  ? 'This request is closed.'
                  : resolutionProcessingUnavailable
                  ? 'Review the recorded history before coordinating the next step outside ShipAid.'
                  : defectRequestClosed
                  ? 'This request is closed.'
                  : defectReviewBlocked && displayedNextAction !== 'AWAITING_CUSTOMER_INFORMATION'
                  ? 'Complete the evidence review before choosing a resolution.'
                  : getNextActionDescription(displayedNextAction)}
              </p>
              {returnData.deadlineAt && (
                <p className="mt-3 text-xs font-semibold text-blue-700">
                  Due {formatWorkflowDate(returnData.deadlineAt)}
                </p>
              )}
            </div>
          </div>

          {hasActionableReviewSignals && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <p className="mr-1 text-xs font-bold uppercase tracking-wider text-amber-800">
                  Review signals
                </p>
                {returnData.reviewRequired && (
                  <span className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-800">
                    Review required
                  </span>
                )}
                {workflowReviewReasons.map((reason) => (
                  <span
                    key={reason}
                    className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-medium text-gray-700"
                  >
                    {formatWorkflowLabel(reason)}
                  </span>
                ))}
              </div>

              {workflowRuleFlags.length > 0 && (
                <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                  {workflowRuleFlags.map((flag, index) => (
                    <div
                      key={
                        typeof flag === 'string'
                          ? flag
                          : flag.code || flag.label || index
                      }
                      title={getRuleFlagTitle(flag)}
                      className={`rounded-md border px-3 py-2 ${getReviewSignalClasses(
                        flag.severity || returnData.riskLevel,
                      )}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">
                            {getRuleFlagLabel(flag)}
                          </p>
                          {flag.message && (
                            <p className="mt-1 text-xs leading-relaxed">
                              {flag.message}
                            </p>
                          )}
                        </div>
                        {!resolutionProcessingUnavailable && flag.code && (
                          <button
                            type="button"
                            aria-label={`Resolve ${getRuleFlagLabel(flag)}`}
                            onClick={() => handleResolveRuleFlag(flag.code)}
                            disabled={Boolean(resolvingRuleFlagCode)}
                            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {resolvingRuleFlagCode === flag.code
                              ? 'Resolving'
                              : 'Resolve'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {returnData.settlementFailureReason && (
                <div className="mt-3 rounded-md border border-red-200 bg-white px-3 py-2 text-red-700">
                  <p className="text-xs font-bold uppercase tracking-wider">
                    Settlement failure
                  </p>
                  <p className="mt-1 text-sm">
                    {returnData.settlementFailureReason}
                  </p>
                </div>
              )}
            </div>
          )}

          {workflowDetails.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                Key details
              </h3>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {workflowDetails.map((detail) => (
                  <div
                    key={detail.label}
                    className="rounded-md border border-gray-200 px-3 py-2"
                  >
                    <p className="text-xs text-gray-500">{detail.label}</p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-900">
                      {detail.value}
                    </p>
                    {detail.subValue && (
                      <p className="mt-1 text-xs text-gray-500">
                        {detail.subValue}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <details className="rounded-lg border border-gray-200 bg-white">
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700">
              <span>Recent activity</span>
              <span className="text-xs font-normal text-gray-500">
                {workflowEvents.length}{' '}
                {workflowEvents.length === 1 ? 'event' : 'events'}
              </span>
            </summary>
            <div className="border-t border-gray-200">
              {workflowEvents.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {workflowEvents.map((event) => {
                    const payloadSummary = getEventPayloadSummary(
                      event.type,
                      event.payload,
                    );
                    return (
                      <div
                        key={event.id}
                        className="flex gap-3 px-4 py-3 text-sm"
                      >
                        <div className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-500" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-semibold text-gray-900">
                              {getWorkflowEventLabel(event.type)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatWorkflowDate(event.occurredAt)}
                            </p>
                          </div>
                          {payloadSummary && (
                            <p className="mt-1 break-words text-xs text-gray-600">
                              {payloadSummary}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="px-4 py-3 text-sm text-gray-500">
                  No activity recorded yet.
                </p>
              )}
            </div>
          </details>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Return Items */}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Request items</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {returnData.returnItems.map((item) => (
                <div key={item.id} className="px-6 py-5 flex items-center">
                  <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
                    <img
                      src={
                        item.orderItem?.imageSrc ||
                        'https://via.placeholder.com/64'
                      }
                      alt={item.orderItem?.title || 'Unavailable item'}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  <div className="ml-5 flex-1 text-sm">
                    <div className="flex justify-between">
                      <h4 className="font-semibold text-gray-900">
                        {item.orderItem?.title || 'Item information unavailable'}
                      </h4>
                      <p className="font-medium text-gray-900">
                        {item.orderItem ? formatCurrency(item.orderItem.price) : 'Amount unavailable'}
                      </p>
                    </div>
                    <p className="text-gray-500 mt-0.5">
                      SKU: {item.orderItem?.sku || 'Unavailable'}
                    </p>
                    <div className="mt-2 flex items-center gap-4">
                      <span className="text-gray-600">
                        Quantity:{' '}
                        <span className="font-medium text-gray-900">
                          {item.quantity}
                        </span>
                      </span>
                    </div>
                    {item.reason && (
                      <div className="mt-3 bg-gray-50 p-3 rounded text-gray-600">
                        <span className="font-semibold text-gray-700">
                          Reason:{' '}
                        </span>
                        {formatReturnReason(item.reason)}
                      </div>
                    )}
                    {item.reasonComments && (
                      <p className="mt-2 whitespace-pre-wrap text-gray-700">
                        {item.reasonComments}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Notes */}
          {returnData.customerNotes && (
            <div className="border rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Customer Notes</h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {returnData.customerNotes}
                </p>
              </div>
            </div>
          )}

          {/* Denial Info */}
          {returnData.status === ReturnStatus.DENIED &&
            returnData.denialReason && (
              <div className="border rounded-lg overflow-hidden border-red-200">
                <div className="px-6 py-4 bg-red-50 border-b border-red-200">
                  <h3 className="font-semibold text-red-800">
                    Denial Information
                  </h3>
                </div>
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Reason
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {returnData.denialReason}
                    </p>
                  </div>
                  {returnData.denialDescription && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                        Description
                      </p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {returnData.denialDescription}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* New Info Sections */}
          {!keepCreditManaged && (defectReviewApproved || (resolutionProcessingUnavailable && hasRecordedSettlement)) && (
            <DefectResolutionPanel
              returnRecord={returnData}
              fallbackCurrency={returnData.order.paymentCurrencyCode || currency}
            />
          )}
          {!keepCreditManaged && !isDefectReturn && (
          <div className="space-y-6">
            {['KEEP_ITEM_DISCOUNT', 'STORE_CREDIT_BONUS'].includes(returnData.deflectionOption || '') && (
              <section>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                  Selected Offer
                </h4>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-4 shadow-sm">
                  <div className="bg-white p-2.5 rounded-lg shadow-sm">
                    <span className="text-2xl">🎁</span>
                  </div>
                  <div className="flex-1">
                    <h5 className="font-bold text-gray-900">
                      {plainStoreCredit ? 'Store credit' : selectedOffer?.label ||
                        (returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
                          ? 'Keep Item Discount'
                          : 'Store Credit Bonus')}
                    </h5>
                    <p className="text-sm text-gray-600">
                      {returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
                        ? 'Customer has opted to keep their items and receive a discount code/credit.'
                        : plainStoreCredit ? 'Customer has opted for store credit.'
                        : 'Customer has opted for store credit with a bonus value.'}
                    </p>
                    {hasDeflectionReward && (
                      <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-green-700">
                          Issued incentive
                        </p>
                        <div className="mt-1 flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                          <span className="font-medium text-gray-900">
                            {deflectionRewardOption === 'DISCOUNT_CODE'
                              ? 'Discount code'
                              : 'Store credit'}
                          </span>
                          {Number.isFinite(deflectionRewardAmount) &&
                            deflectionRewardAmount > 0 && (
                              <span className="font-bold text-green-700">
                                {getStoreCurrencyFormatter(
                                  deflectionRewardOption === 'STORE_CREDIT'
                                    ? returnMeta.storeCreditCurrency || currency
                                    : currency,
                                  deflectionRewardAmount,
                                )}
                              </span>
                            )}
                        </div>
                        {deflectionRewardCode && (
                          <p className="mt-2 text-sm text-gray-700">
                            Code:{' '}
                            <span className="font-mono font-bold text-gray-900">
                              {deflectionRewardCode}
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {!resolutionProcessingUnavailable && !financialReviewRequired && <section>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                {storeCreditRequested ? 'Estimated Store Credit' : 'Estimated Refund'}
              </h4>
              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-6 shadow-sm">
                {approvalInsight && (
                  <div
                    className={`mb-4 rounded-xl border px-4 py-3 ${approvalInsightClasses}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={classNames(
                          'rounded-full p-0.5 border bg-white',
                          approvalInsight.tone === 'green'
                            ? 'border-green-200'
                            : 'border-amber-200',
                        )}
                      >
                        {approvalInsight.tone === 'green' ? (
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M10.29 3.86l-7.17 12.42A2 2 0 004.85 19h14.3a2 2 0 001.73-2.72L13.71 3.86a2 2 0 00-3.42 0z"
                            />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">
                          {approvalInsight.title}
                        </p>
                        <p className="text-xs leading-relaxed mt-1">
                          {approvalInsight.description}
                        </p>
                        <p className="text-[11px] uppercase tracking-[0.08em] mt-2 opacity-80">
                          Configured mode:{' '}
                          {POLICY_MODE_LABELS[configuredPolicyMode]}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex justify-between text-gray-600 text-sm">
                    <span>Item subtotal</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(itemSubtotal)}
                    </span>
                  </div>
                  {dynamicFees.map((fee, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between text-gray-600 text-sm"
                    >
                      <span>{fee.label}</span>
                      <span className="font-medium text-red-600">
                        -{formatCurrency(feeAmount(fee))}
                      </span>
                    </div>
                  ))}

                  {returnData.deflectionOption && !plainStoreCredit && (
                    <>
                      <div className="flex justify-between text-gray-600 text-sm pt-2 border-t border-blue-100 italic">
                        <span>Base Net Total</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(baseNetAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-indigo-600 font-medium">
                          {returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
                            ? 'Keep Item Discount'
                            : 'Store Credit Bonus'}
                          {selectedOffer?.value
                            ? ` (${selectedOffer.value}${
                                selectedOffer.valueType === 'PERCENTAGE'
                                  ? '%'
                                  : '$'
                              })`
                            : ''}
                        </span>
                        <span
                          className={`font-bold ${
                            returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
                              ? 'text-blue-600'
                              : deflectionAdjustment >= 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          {returnData.deflectionOption === 'KEEP_ITEM_DISCOUNT'
                            ? selectedOffer
                              ? formatCurrency(totalRefundAmount)
                              : '+$0.00'
                            : `${
                                deflectionAdjustment >= 0 ? '+' : ''
                              }${formatCurrency(deflectionAdjustment)}`}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="pt-4 border-t border-blue-200/50 flex justify-between items-end">
                    <span className="font-bold text-gray-800">
                      {approvedInspectionAmount !== undefined ? 'Approved settlement amount' : returnData.deflectionOption && !plainStoreCredit
                        ? 'Final Adjusted Amount'
                        : storeCreditRequested ? 'Total Store Credit' : 'Total Refund Amount'}
                    </span>
                    <span className="text-3xl font-bold text-blue-600">
                      {formatCurrency(approvedInspectionAmount ?? totalRefundAmount)}
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-xs text-gray-400 italic">
                  {approvedInspectionAmount !== undefined
                    ? returnHasScheduledInspectionSettlement(returnData) ? 'Inspection approved. Settlement follows your schedule.' : 'Inspection decision saved. Issue the requested resolution separately.'
                    : 'This is an estimate. Final refund amount will be confirmed after review.'}
                </p>
              </div>
            </section>}
          </div>
          )}
        </div>

        <div className="lg:col-span-1 space-y-8">
          {/* Customer Info Card */}
          <div className="border rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 text-lg">Customer</h3>
              <UserIcon className="w-5 h-5 text-gray-400" />
            </div>

            <div className="space-y-4">
              <div className="border-b border-gray-100 pb-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Order Number
                </p>
                <Link
                  to={withRequestReturnTo(
                    `/orders/${returnData.orderId}`,
                    returnTo,
                  )}
                  className="text-blue-600 font-medium hover:underline flex items-center gap-1"
                >
                  {returnData.order.number}
                </Link>
              </div>

              <div className="border-b border-gray-100 pb-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Customer Name
                </p>
                <p className="text-sm text-gray-900 font-medium">
                  {returnData.order.customerName}
                </p>
              </div>

              <div className="border-b border-gray-100 pb-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Email
                </p>
                <p className="text-sm text-gray-900 font-medium">
                  {returnData.order.customerEmail ?? 'N/A'}
                </p>
              </div>

              <div className="border-b border-gray-100 pb-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Phone
                </p>
                <p className="text-sm text-gray-900 font-medium">
                  {returnData.order.customerPhone ?? 'N/A'}
                </p>
              </div>

              <div className="pb-1">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  {keepCreditManaged ? 'Original order total' : returnData.requestedResolution === 'EXCHANGE' || nativeIncentiveManaged ? 'Current order total' : 'Order Total'}
                </p>
                <p className="text-sm text-gray-900 font-bold">
                  {keepCreditManaged ? formatCurrency(returnData.order.total) : returnData.requestedResolution === 'EXCHANGE' || nativeIncentiveManaged
                    ? storeId && returnId && nativeReviewCandidate && nativeStatusLoading
                      ? 'Checking current order total…'
                      : nativeExchangeStatus?.currentOrderTotal && nativeExchangeStatus.currentOrderTotal.currency === returnData.order.currencyCode
                      ? `${nativeExchangeStatus.currentOrderTotal.currency} ${nativeExchangeStatus.currentOrderTotal.amount}`
                      : 'Current order total unavailable'
                    : formatCurrency(returnData.order.total)}
                </p>
              </div>

              {returnData.deflectionOption && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Deflection Option
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                    <p className="text-sm text-indigo-700 font-bold">
                      {plainStoreCredit ? 'Store credit' : returnData.deflectionOption.replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {shouldShowShopifyReturnSyncCard && (
            <div className="border rounded-lg p-5">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    Shopify Return Sync
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Native Shopify return record for connected WMS and OMS
                    systems.
                  </p>
                </div>
                <span
                  className={classNames(
                    'inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-semibold',
                    shopifyReturnSyncStatusClass,
                  )}
                >
                  {shopifyReturnSyncPresentation.statusLabel}
                </span>
              </div>

              <div className="space-y-3">
                <div className="border-b border-gray-100 pb-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Native return
                  </p>
                  <p className="text-sm text-gray-900 font-medium">
                    {returnData.shopifyReturnName ||
                      returnData.shopifyReturnId ||
                      shopifyReturnSyncPresentation.nativeReturnFallback}
                  </p>
                </div>
                <div className="border-b border-gray-100 pb-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Shopify status
                  </p>
                  <p className="text-sm text-gray-900 font-medium">
                    {formatWorkflowLabel(returnData.shopifyReturnStatus)}
                  </p>
                </div>
                <div className="border-b border-gray-100 pb-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Last synced
                  </p>
                  <p className="text-sm text-gray-900 font-medium">
                    {formatWorkflowDate(returnData.shopifyReturnSyncedAt)}
                  </p>
                </div>
                <div className="border-b border-gray-100 pb-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Last webhook
                  </p>
                  <p className="text-sm text-gray-900 font-medium">
                    {formatWorkflowDate(returnData.shopifyReturnLastWebhookAt)}
                  </p>
                </div>
                {returnData.shopifyReverseFulfillmentOrderIds?.length ? (
                  <div className="border-b border-gray-100 pb-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Reverse fulfillment orders
                    </p>
                    <div className="space-y-1">
                      {returnData.shopifyReverseFulfillmentOrderIds.map(
                        (reverseFulfillmentOrderId) => (
                          <p
                            key={reverseFulfillmentOrderId}
                            className="break-all text-xs font-mono text-gray-700"
                          >
                            {reverseFulfillmentOrderId}
                          </p>
                        ),
                      )}
                    </div>
                  </div>
                ) : null}
                {returnData.shopifyReturnSyncError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-red-700">
                      Sync error
                    </p>
                    <p className="mt-1 break-words text-sm text-red-700">
                      {getShopifyReturnSyncErrorMessage(
                        returnData.shopifyReturnSyncError,
                      )}
                    </p>
                  </div>
                )}
                {canRetryShopifyReturnSync && (
                  <Button
                    onClick={handleRetryShopifyReturnSync}
                    isLoading={retryingShopifyReturnSync}
                    variant="outlined"
                    className="w-full justify-center text-xs text-blue-700"
                  >
                    Retry Shopify sync
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Shipping Info Card */}
          {!defectReviewBlocked && !defectPlanShippingBlocked && hasShippingInfo && (
            <div className="border rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 text-lg mb-4">
                Shipping Information
              </h3>
              <div className="space-y-4">
                {hasSelectedShippingOption && (
                  <div className="border-b border-gray-100 pb-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Selected shipping method
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {selectedShippingOptionLabel}
                    </p>
                    {selectedShippingOptionSourceLabel && (
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedShippingOptionSourceLabel}
                      </p>
                    )}
                  </div>
                )}
                {returnData.shippingId && (
                  <div className="border-b border-gray-100 pb-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Shipping ID
                    </p>
                    <p className="text-sm text-gray-900 font-mono font-medium">
                      {returnData.shippingId}
                    </p>
                  </div>
                )}
                {returnData.trackingNumber && (
                  <div className="border-b border-gray-100 pb-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Tracking Number
                    </p>
                    <TrackingLink
                      trackingNumber={returnData.trackingNumber}
                      ownerType="RETURN"
                      ownerId={returnData.id}
                      className="text-sm font-mono font-medium text-blue-600 underline hover:text-blue-700"
                    />
                  </div>
                )}
                {(returnData.carrier || returnData.service) && (
                  <div className="border-b border-gray-100 pb-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Carrier / Service
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {[returnData.carrier, returnData.service]
                        .filter(Boolean)
                        .join(' / ')}
                    </p>
                  </div>
                )}
                {hasLabelCost && (
                  <div className="border-b border-gray-100 pb-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Return label/shipping cost
                    </p>
                    <p className="text-sm text-gray-900 font-bold">
                      {getStoreCurrencyFormatter(returnData.meta?.label?.currency || currency, Number(returnData.labelCost))}
                    </p>
                  </div>
                )}
                {labelSource && (
                  <div className="border-b border-gray-100 pb-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Label Source
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {labelSource}
                    </p>
                  </div>
                )}
                {returnData.labelUrl && (
                  <div className="pt-1">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Return label
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleOpenReturnLabel}
                        className="bg-blue-600 text-white hover:bg-blue-700 text-xs py-2"
                      >
                        Open label
                      </Button>
                      {!resolutionProcessingUnavailable && <Button
                        onClick={handleSendReturnLabel}
                        isLoading={sendingReturnLabel}
                        variant="outlined"
                        className="text-xs text-blue-700"
                      >
                        Send to Customer
                      </Button>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal open={denyModalOpen && !keepCreditManaged} openModal={setDenyModalOpen}>
        <DenyReturn handleDeny={handleDeny} loading={updatingStatus} />
      </Modal>
      <Modal open={approvalModalOpen && !resolutionProcessingUnavailable} openModal={setApprovalModalOpen}>
        {approvalModalOpen && !resolutionProcessingUnavailable && returnId && storeId && (
          <ReturnApprovalConfirmation
            returnId={returnId}
            storeId={storeId}
            pending={updatingStatus}
            onCancel={() => setApprovalModalOpen(false)}
            onConfirm={confirmApproval}
          />
        )}
      </Modal>
    </div>
  );
});
